/**
 * 任务管理页面（新版）- 三栏布局
 * 左：知识管理列表（多选）
 * 中：用户列表（多选）- 包含教师和学生
 * 右：任务分配设置
 */

import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  BookOpen,
  Search,
  FileText,
  FileSearch,
  Users,
  Check,
  Loader2,
  X,
  Sparkles,
  ClipboardCheck,
  FileEdit,
  Edit,
  Trash2,
  BarChart3,
  Play,
  Calendar,
  Clock,
  Eye,
  Plus,
  ChevronUp,
  ChevronDown,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../components/Toast';
import { useConfirm } from '../components/ConfirmModal';
import { api, getErrorMessage } from '../services/api';
import {
  KnowledgeArchive,
  User,
  ExamPaper,
  ExamPaperWithQuestions,
  LLMExamGenerate,
  LearningTaskCreate,
  ExamTaskCreate,
  LearningTask,
  ExamTask,
  TaskStudentsCompletion,
  LearningTaskUpdate,
  ExamTaskUpdate,
} from '../types/api';
import PaperEditor from '../components/PaperEditor';
import styles from './TaskManagement.module.css';

// 难度配置
const DIFFICULTY_CONFIG: Record<string, { label: string; color: string; bgColor: string }> = {
  easy: { label: '简单', color: '#22c55e', bgColor: '#22c55e15' },
  medium: { label: '中等', color: '#f59e0b', bgColor: '#f59e0b15' },
  hard: { label: '困难', color: '#ef4444', bgColor: '#ef444415' },
};

// 获取考试任务状态
const getTaskStatus = (startTime: string, endTime: string): 'pending' | 'ongoing' | 'ended' => {
  const now = new Date();
  const start = new Date(startTime);
  const end = new Date(endTime);

  if (now < start) return 'pending';
  if (now > end) return 'ended';
  return 'ongoing';
};

// 获取学习任务状态
const getLearningTaskStatus = (deadline: string): 'ongoing' | 'ended' => {
  const now = new Date();
  const end = new Date(deadline);
  return now > end ? 'ended' : 'ongoing';
};

export default function TaskManagementNewPage() {
  const { user, hasPermission } = useAuth();
  const { showToast } = useToast();
  const confirmDialog = useConfirm();
  const canManage = hasPermission('任务管理');

  // 辅助函数：判断文件类型是否为文档类型（可设置学习时长）
  const isDocumentFileType = (fileType: string) => {
    const documentTypes = ['pdf', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx'];
    return documentTypes.includes(fileType?.toLowerCase());
  };

  // 加载状态
  const [loading, setLoading] = useState(true);
  const [assigning, setAssigning] = useState(false);

  // AbortController 用于取消 AI 生成请求
  const abortControllerRef = useRef<AbortController | null>(null);

  // 档案相关
  const [archives, setArchives] = useState<KnowledgeArchive[]>([]);
  const [archiveSearch, setArchiveSearch] = useState('');
  const [selectedArchiveIds, setSelectedArchiveIds] = useState<number[]>([]);

  // 档案文件展开和选择相关
  const [expandedArchives, setExpandedArchives] = useState<number[]>([]); // 展开的档案ID列表
  const [selectedFileIds, setSelectedFileIds] = useState<number[]>([]); // 选中的文件ID列表

  // 学生相关
  const [students, setStudents] = useState<User[]>([]);
  const [studentSearch, setStudentSearch] = useState('');
  const [selectedStudentIds, setSelectedStudentIds] = useState<number[]>([]);

  // 试卷相关
  const [papers, setPapers] = useState<ExamPaper[]>([]);

  // 任务名称
  const [taskName, setTaskName] = useState('');

  // 学习任务表单
  const [learningTaskForm, setLearningTaskForm] = useState({
    deadline: '',
    task_desc: '',
  });

  // 考试任务表单
  const [examTaskForm, setExamTaskForm] = useState({
    paper_id: 0,
    exam_start_time: '',
    exam_end_time: '',
    task_desc: '',
  });

  // 弹窗状态
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const [showGeneratePaperModal, setShowGeneratePaperModal] = useState(false);
  const [generateMode, setGenerateMode] = useState<'manual' | 'ai' | null>(null);
  const [generatingArchive, setGeneratingArchive] = useState<KnowledgeArchive | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generateProgress, setGenerateProgress] = useState(0);
  const [generateStatus, setGenerateStatus] = useState('');
  const progressIntervalRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // AI生成试卷配置
  const [aiConfig, setAiConfig] = useState<LLMExamGenerate>({
    archive_id: 0,
    paper_name: '',
    question_count: 10,
    question_types: ['single', 'multiple', 'judgment', 'essay'],
    difficulty: 'medium',
    total_score: 100,
    pass_score: 60,
    single_choice_config: { count: 0, easy: 0, medium: 0, hard: 0 },
    multiple_choice_config: { count: 0, easy: 0, medium: 0, hard: 0 },
    judgment_config: { count: 0, easy: 0, medium: 0, hard: 0 },
    essay_config: { count: 0, easy: 0, medium: 0, hard: 0 },
  });

  // AI生成模式：generate=AI生成试题, extract=AI识别题目
  const [aiGenerateMode, setAiGenerateMode] = useState<'generate' | 'extract'>('generate');

  // AI生成试卷 - 档案文件选择状态（支持跨档案多选文件）
  const [aiExpandedArchives, setAiExpandedArchives] = useState<number[]>([]); // AI弹窗中展开的档案ID列表
  const [aiSelectedFileIds, setAiSelectedFileIds] = useState<{ file_id: number; archive_id: number }[]>([]); // AI弹窗中选中的文件列表（含档案ID）
  const [aiArchiveSearch, setAiArchiveSearch] = useState(''); // AI弹窗中的档案搜索

  // AI识别题目 - 本地上传文件（extract 模式不再从档案选取）
  const [aiLocalFiles, setAiLocalFiles] = useState<File[]>([]);

  // AI生成支持的文件格式
  const SUPPORTED_AI_EXTENSIONS = ['.txt', '.csv', '.pdf', '.docx', '.doc', '.xlsx', '.xls', '.pptx', '.ppt', '.srt'];
  const getFileExtension = (fileName: string) => {
    const lastDot = fileName.lastIndexOf('.');
    return lastDot >= 0 ? fileName.slice(lastDot).toLowerCase() : '';
  };
  const getUnsupportedFiles = (fileIds: { file_id: number; archive_id: number }[]) => {
    const unsupported: string[] = [];
    for (const f of fileIds) {
      const archive = archives.find(a => a.archive_id === f.archive_id);
      const file = archive?.files?.find(ff => ff.file_id === f.file_id);
      if (file) {
        const ext = getFileExtension(file.file_name);
        if (ext && !SUPPORTED_AI_EXTENSIONS.includes(ext)) {
          unsupported.push(file.file_name);
        }
      }
    }
    return unsupported;
  };

  // AI识别题目 - 本地文件选择与移除
  const handleAiFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = Array.from(e.target.files || []);
    if (selected.length === 0) return;
    setAiLocalFiles(prev => [...prev, ...selected]);
    // 清空 input，允许重复选择同一文件
    e.target.value = '';
  };

  const removeAiLocalFile = (idx: number) => {
    setAiLocalFiles(prev => prev.filter((_, i) => i !== idx));
  };

  // 格式化文件大小
  const formatFileSize = (bytes: number | null | undefined): string => {
    if (!bytes) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
  };

  // 手动创建试卷表单
  const [manualPaperForm, setManualPaperForm] = useState({
    paper_name: '',
    exam_duration: 60,
    paper_desc: '',
  });

  // 左栏事项类型：知识管理或试卷
  const [itemType, setItemType] = useState<'archive' | 'paper'>('archive');

  // 监听事项选择变化，自动填充任务名称
  // 切换事项类型
  const toggleItemType = (type: 'archive' | 'paper') => {
    setItemType(type);
    setValidationErrors({});
  };

  // 任务总览标签页状态
  const [activeTab, setActiveTab] = useState<'allocation' | 'overview' | 'papers'>('overview');
  const [selectedArchiveForPapers, setSelectedArchiveForPapers] = useState<KnowledgeArchive | null>(null);

  // 试卷管理相关状态
  const [papersLoading, setPapersLoading] = useState(false); // 修复：默认设为false，因为试卷已在上方加载
  const [paperSearch, setPaperSearch] = useState('');

  // 试卷分页状态
  const [currentPaperPage, setCurrentPaperPage] = useState(1);
  const papersPerPage = 8; // 每页显示8份试卷

  // 批量删除相关状态
  const [selectedPapers, setSelectedPapers] = useState<number[]>([]);
  const [isBatchDeleteModalOpen, setIsBatchDeleteModalOpen] = useState(false);

  // 编辑模式
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingTaskId, setEditingTaskId] = useState<number | null>(null);

  // 分配模态框状态
  const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);
  const [assignModalType, setAssignModalType] = useState<'learning' | 'exam'>('exam');
  const [assigningTaskId, setAssigningTaskId] = useState<number | null>(null);
  const [users, setUsers] = useState<User[]>([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [selectedUserIds, setSelectedUserIds] = useState<number[]>([]);

  // 试卷查看状态
  const [isPaperViewerOpen, setIsPaperViewerOpen] = useState(false);
  const [viewingPaper, setViewingPaper] = useState<ExamPaperWithQuestions | null>(null);

  // 试卷信息查看状态（只显示基本信息，不显示题目）
  const [isPaperInfoOpen, setIsPaperInfoOpen] = useState(false);
  const [viewingPaperInfo, setViewingPaperInfo] = useState<ExamPaper | null>(null);

  // 试卷编辑状态
  const [isPaperEditorOpen, setIsPaperEditorOpen] = useState(false);
  const [editingPaper, setEditingPaper] = useState<ExamPaperWithQuestions | null>(null);

  // 查看知识管理文件状态
  const [isViewFilesModalOpen, setIsViewFilesModalOpen] = useState(false);
  const [selectedArchiveForFiles, setSelectedArchiveForFiles] = useState<KnowledgeArchive | null>(null);

  // 手动创建试卷表单
  const [paperForm, setPaperForm] = useState({
    paper_name: '',
    paper_desc: '',
    total_score: 100,
    pass_score: 60,
    exam_duration: 60,
  });
  const [savingPaper, setSavingPaper] = useState(false);

  // 任务详情弹窗状态
  const [isTaskDetailOpen, setIsTaskDetailOpen] = useState(false);
  const [selectedTaskDetail, setSelectedTaskDetail] = useState<AssignedTaskOverview | null>(null);
  // 查看详情模态框的文件信息
  const [viewDetailFiles, setViewDetailFiles] = useState<Array<{
    file_id: number;
    file_name: string;
    file_type: string;
    archive_name: string;
    required_duration: number;
  }>>([]);
  const [viewDetailFilesLoading, setViewDetailFilesLoading] = useState(false);

  // 编辑任务弹窗状态
  const [isEditTaskOpen, setIsEditTaskOpen] = useState(false);
  const [editingTask, setEditingTask] = useState<AssignedTaskOverview | null>(null);
  const [editTaskForm, setEditTaskForm] = useState({
    task_name: '',
    deadline: '',
    exam_start_time: '',
    exam_end_time: '',
    task_desc: '',
  });
  const [savingTask, setSavingTask] = useState(false);

  // 编辑任务时加载的文件信息（仅学习任务）
  const [editTaskFiles, setEditTaskFiles] = useState<Array<{
    file_id: number;
    file_name: string;
    file_type: string;
    file_size: number | null;
    archive_id: number;
    archive_name: string;
    required_duration: number;
  }>>([]);
  const [filesLoading, setFilesLoading] = useState(false);

  // 编辑任务中的学生管理
  const [assignedStudents, setAssignedStudents] = useState<Array<AssignedStudent & { role_name?: string; department?: string }>>([]);
  const [availableStudents, setAvailableStudents] = useState<User[]>([]);
  const [selectedToAdd, setSelectedToAdd] = useState<number[]>([]);
  const [selectedToRemove, setSelectedToRemove] = useState<number[]>([]);
  const [studentsLoading, setStudentsLoading] = useState(false);

  // 搜索功能
  const [assignedSearch, setAssignedSearch] = useState('');
  const [availableSearch, setAvailableSearch] = useState('');

  // 过滤后的学生列表
  const filteredAssignedStudents = assignedStudents.filter(
    (student) =>
      student.user_name.toLowerCase().includes(assignedSearch.toLowerCase()) ||
      student.account.toLowerCase().includes(assignedSearch.toLowerCase()) ||
      (student.department || '').toLowerCase().includes(assignedSearch.toLowerCase())
  );

  const filteredAvailableStudents = availableStudents.filter(
    (student) =>
      student.user_name.toLowerCase().includes(availableSearch.toLowerCase()) ||
      student.account.toLowerCase().includes(availableSearch.toLowerCase()) ||
      (student.dept_name || '').toLowerCase().includes(availableSearch.toLowerCase())
  );

  // 定义任务总览数据结构
  type AssignedTaskOverview = {
    task_info: LearningTask | ExamTask;
    task_type: 'learning' | 'exam';
    task_id: number;
    task_name: string;
    total_students: number;
    completed_count: number;
    passed_count: number | null;
    students: any[]; // StudentCompletionInfo[]，简化处理
  };

  const [assignedTasks, setAssignedTasks] = useState<AssignedTaskOverview[]>([]);
  const [tasksLoading, setTasksLoading] = useState(false);

  // 任务总览筛选状态
  const [taskTypeFilter, setTaskTypeFilter] = useState<'all' | 'learning' | 'exam'>('all');
  const [taskStatusFilter, setTaskStatusFilter] = useState<'all' | 'ongoing' | 'pending' | 'ended'>('all');
  const [taskSearchFilter, setTaskSearchFilter] = useState('');

  // 分页状态
  const [currentPage, setCurrentPage] = useState(1);
  const tasksPerPage = 8; // 每页显示8条任务

  // 筛选后的任务列表
  const filteredTasks = assignedTasks.filter((taskData) => {
    // 任务类型筛选
    if (taskTypeFilter !== 'all' && taskData.task_type !== taskTypeFilter) {
      return false;
    }

    // 状态筛选
    if (taskStatusFilter !== 'all') {
      const status = taskData.task_type === 'learning'
        ? getLearningTaskStatus((taskData.task_info as LearningTask).deadline)
        : getTaskStatus((taskData.task_info as ExamTask).exam_start_time, (taskData.task_info as ExamTask).exam_end_time);
      if (status !== taskStatusFilter) {
        return false;
      }
    }

    // 搜索筛选
    if (taskSearchFilter.trim()) {
      const searchLower = taskSearchFilter.toLowerCase();
      if (!taskData.task_name.toLowerCase().includes(searchLower)) {
        return false;
      }
    }

    return true;
  });

  // 排序：已结束的任务放最后
  const sortedTasks = [...filteredTasks].sort((a, b) => {
    const statusA = a.task_type === 'learning'
      ? getLearningTaskStatus((a.task_info as LearningTask).deadline)
      : getTaskStatus((a.task_info as ExamTask).exam_start_time, (a.task_info as ExamTask).exam_end_time);
    const statusB = b.task_type === 'learning'
      ? getLearningTaskStatus((b.task_info as LearningTask).deadline)
      : getTaskStatus((b.task_info as ExamTask).exam_start_time, (b.task_info as ExamTask).exam_end_time);

    // 已结束的任务排在最后
    if (statusA === 'ended' && statusB !== 'ended') return 1;
    if (statusA !== 'ended' && statusB === 'ended') return -1;
    return 0;
  });

  // 根据分页计算当前页的任务列表
  const indexOfLastTask = currentPage * tasksPerPage;
  const indexOfFirstTask = indexOfLastTask - tasksPerPage;
  const currentTasks = sortedTasks.slice(indexOfFirstTask, indexOfLastTask);
  const totalPages = Math.ceil(sortedTasks.length / tasksPerPage);

  // 初始化加载数据
  useEffect(() => {
    loadData();
  }, [user?.user_id]);

  // 当切换到任务总览标签页时加载任务数据
  useEffect(() => {
    if (activeTab === 'overview' && user?.user_id) {
      loadAssignedTasks();
    }
  }, [activeTab, user?.user_id]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [archivesRes, usersRes, papersRes] = await Promise.all([
        api.getAssignableArchives(),  // 改用新API获取可分配的档案
        api.getAssignableUsers({ page: 1, page_size: 100 }),  // 获取可分配用户
        api.getExamPapers({ page: 1, page_size: 100, teacher_id: (user?.role_id === 8 || user?.role_name === "超级管理员" ? undefined : user?.user_id) }),  // 只获取自己创建的试卷
      ]);

      if (archivesRes.code === 200 && archivesRes.data) {
        setArchives(archivesRes.data);
      }
      if (usersRes.code === 200 && usersRes.data) {
        setStudents(usersRes.data);
      }
      if (papersRes.code === 200 && papersRes.data) {
        setPapers(papersRes.data);
      }
    } catch (error) {
      console.error('加载数据失败:', error);
      showToast('error', '加载数据失败');
    } finally {
      setLoading(false);
    }
  };

  // 加载任务总览数据
  const loadAssignedTasks = async () => {
    if (!user?.user_id) return;

    setTasksLoading(true);
    try {
      // 获取该教师创建的所有学习任务
      const [learningTasksRes, examTasksRes] = await Promise.all([
        api.getLearningTasks({ teacher_id: (user.role_id === 8 || user.role_name === "超级管理员" ? undefined : user.user_id), page: 1, page_size: 100 }),
        api.getExamTasks({ teacher_id: (user.role_id === 8 || user.role_name === "超级管理员" ? undefined : user.user_id), page: 1, page_size: 100 })
      ]);

      // 并行获取所有任务的完成情况
      const completionPromises: Promise<void>[] = [];

      // 收集学习任务的完成情况请求
      if (learningTasksRes.code === 200 && learningTasksRes.data) {
        for (const task of learningTasksRes.data) {
          completionPromises.push(
            api.getLearningTaskStudentsCompletion(task.learning_task_id)
              .then(completionRes => {
                if (completionRes.code === 200 && completionRes.data) {
                  return {
                    ...completionRes.data,
                    task_info: task,
                    task_type: 'learning' as const
                  };
                }
                return null;
              })
              .catch(error => {
                console.error(`获取学习任务 ${task.learning_task_id} 完成情况失败:`, error);
                return null;
              })
          );
        }
      }

      // 收集考试任务的完成情况请求
      if (examTasksRes.code === 200 && examTasksRes.data) {
        for (const task of examTasksRes.data) {
          completionPromises.push(
            api.getExamTaskStudentsCompletion(task.exam_task_id)
              .then(completionRes => {
                if (completionRes.code === 200 && completionRes.data) {
                  return {
                    ...completionRes.data,
                    task_info: task,
                    task_type: 'exam' as const
                  };
                }
                return null;
              })
              .catch(error => {
                console.error(`获取考试任务 ${task.exam_task_id} 完成情况失败:`, error);
                return null;
              })
          );
        }
      }

      // 并行执行所有请求
      const results = await Promise.all(completionPromises);
      const allTasksWithCompletion = results.filter((item): item is NonNullable<typeof item> => item !== null);

      setAssignedTasks(allTasksWithCompletion);
    } catch (error) {
      console.error('加载任务总览失败:', error);
      showToast('error', '加载任务总览失败');
    } finally {
      setTasksLoading(false);
    }
  };

  // 筛选档案
  const filteredArchives = archives.filter((archive) =>
    archive.archive_name.toLowerCase().includes(archiveSearch.toLowerCase())
  );

  // 筛选用户（支持按用户名、账号、角色、部门搜索）
  const filteredStudents = students.filter((student) =>
    student.user_name.toLowerCase().includes(studentSearch.toLowerCase()) ||
    student.account.toLowerCase().includes(studentSearch.toLowerCase()) ||
    (student.role_name?.toLowerCase().includes(studentSearch.toLowerCase())) ||
    (student.dept_name?.toLowerCase().includes(studentSearch.toLowerCase()))
  );

  // 切换档案选择（多选模式）- 选中档案时默认选中所有文件
  const toggleArchiveSelection = (archiveId: number) => {
    const isSelected = selectedArchiveIds.includes(archiveId);
    let newSelection: number[];

    if (isSelected) {
      // 取消选中档案
      newSelection = selectedArchiveIds.filter((id) => id !== archiveId);
      // 同时取消该档案下的所有文件选择
      const archive = archives.find(a => a.archive_id === archiveId);
      if (archive && archive.files) {
        const archiveFileIds = archive.files.map(f => f.file_id);
        setSelectedFileIds(prev => prev.filter(id => !archiveFileIds.includes(id)));
      }
    } else {
      // 选中档案
      newSelection = [...selectedArchiveIds, archiveId];
      // 同时选中该档案下的所有文件
      const archive = archives.find(a => a.archive_id === archiveId);
      if (archive && archive.files) {
        const archiveFileIds = archive.files.map(f => f.file_id);
        setSelectedFileIds(prev => [...new Set([...prev, ...archiveFileIds])]);
      }
    }

    setSelectedArchiveIds(newSelection);
    setValidationErrors(prev => { const next = {...prev}; delete next.archive; return next; });
  };

  // 切换学生选择
  const toggleStudentSelection = (studentId: number) => {
    setSelectedStudentIds((prev) =>
      prev.includes(studentId)
        ? prev.filter((id) => id !== studentId)
        : [...prev, studentId]
    );
    setValidationErrors(prev => { const next = {...prev}; delete next.students; return next; });
  };

  // 全选/取消全选用户（作用于当前搜索过滤后的可见用户）
  const selectAllStudents = () => {
    if (selectedStudentIds.length === filteredStudents.length && filteredStudents.length > 0) {
      setSelectedStudentIds([]);
    } else {
      setSelectedStudentIds(filteredStudents.map(s => s.user_id));
    }
    setValidationErrors(prev => { const next = {...prev}; delete next.students; return next; });
  };

  // 切换档案展开/收起（显示文件列表）
  const toggleArchiveExpand = (archiveId: number, e: React.MouseEvent) => {
    e.stopPropagation();
    setExpandedArchives((prev) =>
      prev.includes(archiveId)
        ? prev.filter((id) => id !== archiveId)
        : [...prev, archiveId]
    );
  };

  // 切换文件选择 - 选中文件时自动选中档案
  const toggleFileSelection = (fileId: number, archiveId: number, e: React.MouseEvent) => {
    e.stopPropagation();

    const archive = archives.find(a => a.archive_id === archiveId);
    if (!archive) return;

    const isSelected = selectedFileIds.includes(fileId);

    if (isSelected) {
      // 取消选中文件
      setSelectedFileIds(prev => prev.filter(id => id !== fileId));
    } else {
      // 选中文件
      setSelectedFileIds(prev => [...prev, fileId]);

      // 如果档案未选中，自动选中档案
      if (!selectedArchiveIds.includes(archiveId)) {
        setSelectedArchiveIds(prev => [...prev, archiveId]);
      }
    }
  };

  // 获取档案中选中的文件数量
  const getSelectedFileCountForArchive = (archiveId: number) => {
    const archive = archives.find(a => a.archive_id === archiveId);
    if (!archive || !archive.files) return 0;
    return archive.files.filter(f => selectedFileIds.includes(f.file_id)).length;
  };

  // 打开生成试卷弹窗
  const openGeneratePaperModal = (archive: KnowledgeArchive, mode: 'manual' | 'ai') => {
    setGeneratingArchive(archive);
    setGenerateMode(mode);
    setAiConfig({
      archive_id: archive.archive_id,
      paper_name: `${archive.archive_name}-试卷`,
      question_count: 10,
      question_types: ['single', 'multiple', 'judgment', 'essay'],
      difficulty: 'medium',
      total_score: 100,
      pass_score: 60,
      single_choice_config: { count: 0, easy: 0, medium: 0, hard: 0 },
      multiple_choice_config: { count: 0, easy: 0, medium: 0, hard: 0 },
      judgment_config: { count: 0, easy: 0, medium: 0, hard: 0 },
      essay_config: { count: 0, easy: 0, medium: 0, hard: 0 },
    });
    setManualPaperForm({
      paper_name: `${archive.archive_name}-试卷`,
      exam_duration: 60,
      paper_desc: '',
    });
    setShowGeneratePaperModal(true);
  };

  // 处理取消生成
  const handleCancelGenerate = () => {
    if (isGenerating && abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    setShowGeneratePaperModal(false);
  };
  const handleGeneratePaper = async () => {
    try {
      // 检查用户是否已登录
      if (!user || !user.user_id) {
        showToast('error', '用户未登录，请重新登录');
        return;
      }

      if (generateMode === 'ai') {
        if (aiGenerateMode === 'extract') {
          // ===== AI识别题目（本地上传文件，不再从档案选取）=====
          // 验证是否选择了本地文件
          if (aiLocalFiles.length === 0) {
            showToast('warning', '请选择至少一个本地题目文件');
            return;
          }

          // 开始识别，设置进度条
          setIsGenerating(true);
          setGenerateProgress(0);
          setGenerateStatus('正在识别题目文件...');

          // 模拟进度更新
          const statusMessages = [
            { progress: 10, message: '正在读取文件内容...' },
            { progress: 25, message: '正在识别题目结构...' },
            { progress: 40, message: '正在提取单选题...' },
            { progress: 55, message: '正在提取多选题...' },
            { progress: 70, message: '正在提取判断题和填空题...' },
            { progress: 85, message: '正在解析答案和分数...' },
            { progress: 95, message: '正在保存试卷...' },
          ];

          let messageIndex = 0;
          progressIntervalRef.current = setInterval(() => {
            if (messageIndex < statusMessages.length) {
              const { progress, message } = statusMessages[messageIndex];
              setGenerateProgress(progress);
              setGenerateStatus(message);
              messageIndex++;
            }
          }, 8000); // 每8秒更新一次进度

          try {
            // 创建 AbortController 用于取消请求
            abortControllerRef.current = new AbortController();

            const res = await api.extractFromUpload(
              aiLocalFiles,
              aiConfig.paper_name || '',
              user.user_id,
              abortControllerRef.current.signal
            );
            if (res.code === 200 && res.data) {
              setGenerateProgress(100);
              setGenerateStatus('识别完成！');
              showToast('success', 'AI识别题目成功');
              setShowGeneratePaperModal(false);
              setAiLocalFiles([]);
              const papersRes = await api.getExamPapers({ page: 1, page_size: 100, teacher_id: (user?.role_id === 8 || user?.role_name === "超级管理员" ? undefined : user?.user_id) });
              if (papersRes.code === 200 && papersRes.data) {
                setPapers(papersRes.data);
              }
            } else {
              showToast('error', `识别失败: ${res.message || '未知错误'}`);
            }
          } catch (error) {
            console.error('识别题目失败:', error);
            showToast('error', '识别失败: ' + getErrorMessage(error));
          } finally {
            if (progressIntervalRef.current) {
              clearInterval(progressIntervalRef.current);
              progressIntervalRef.current = null;
            }
            // 清理 AbortController
            abortControllerRef.current = null;
            setIsGenerating(false);
            setGenerateProgress(0);
            setGenerateStatus('');
          }
        } else {
          // ===== AI生成试题（从档案选择文件）=====
          // 验证是否选择了文件
          if (aiSelectedFileIds.length === 0) {
            showToast('warning', '请选择至少一个文件');
            return;
          }

          // 计算总题数
          const totalQuestions =
            (aiConfig.single_choice_config?.count || 0) +
            (aiConfig.multiple_choice_config?.count || 0) +
            (aiConfig.judgment_config?.count || 0) +
            (aiConfig.essay_config?.count || 0);

          if (totalQuestions === 0) {
            showToast('warning', '请设置至少一种类型的题目数量');
            return;
          }
          if (totalQuestions > 50) {
            showToast('warning', `题目总数(${totalQuestions})超过上限(50)，请减少题目数量`);
            return;
          }

          // 构建AI生成配置，包含选中的文件信息
          const generateConfig: LLMExamGenerate = {
            ...aiConfig,
            archive_id: aiSelectedFileIds[0]?.archive_id || 0, // 使用第一个文件的档案ID作为主档案
            file_ids: aiSelectedFileIds, // 传递选中的文件列表
            generate_mode: 'generate', // 传递生成模式
          };

          // 开始生成，设置进度条
          setIsGenerating(true);
          setGenerateProgress(0);
          setGenerateStatus('正在分析知识管理内容...');

          // 模拟进度更新
          const statusMessages = [
            { progress: 10, message: '正在分析知识管理内容...（满分100分）' },
            { progress: 25, message: '正在理解知识点...' },
            { progress: 40, message: '正在设计题目...' },
            { progress: 55, message: '正在生成答案...' },
            { progress: 70, message: '正在优化题目质量...' },
            { progress: 85, message: '正在生成答案解析...' },
            { progress: 95, message: '正在保存试卷...' },
          ];

          let messageIndex = 0;
          progressIntervalRef.current = setInterval(() => {
            if (messageIndex < statusMessages.length) {
              const { progress, message } = statusMessages[messageIndex];
              setGenerateProgress(progress);
              setGenerateStatus(message);
              messageIndex++;
            }
          }, 8000); // 每8秒更新一次进度

          try {
            // 创建 AbortController 用于取消请求
            abortControllerRef.current = new AbortController();

            const res = await api.generateExamFromArchive(generateConfig, user.user_id, abortControllerRef.current.signal);
            if (res.code === 200 && res.data) {
              setGenerateProgress(100);
              setGenerateStatus('生成完成！');
              showToast('success', 'AI生成试卷成功');
              setShowGeneratePaperModal(false);
              const papersRes = await api.getExamPapers({ page: 1, page_size: 100, teacher_id: (user?.role_id === 8 || user?.role_name === "超级管理员" ? undefined : user?.user_id) });
              if (papersRes.code === 200 && papersRes.data) {
                setPapers(papersRes.data);
              }
            } else {
              showToast('error', `AI生成失败: ${res.message || '未知错误'}`);
            }
          } catch (error) {
            console.error('生成试卷失败:', error);
            showToast('error', 'AI生成失败: ' + getErrorMessage(error));
          } finally {
            if (progressIntervalRef.current) {
              clearInterval(progressIntervalRef.current);
              progressIntervalRef.current = null;
            }
            // 清理 AbortController
            abortControllerRef.current = null;
            setIsGenerating(false);
            setGenerateProgress(0);
            setGenerateStatus('');
          }
        }
      } else {
        // 手动创建试卷
        if (!generatingArchive) {
          showToast('warning', '请先选择知识管理');
          return;
        }

        setIsGenerating(true);
        try {
          const res = await api.createExamPaper(
            {
              paper_name: manualPaperForm.paper_name,
              archive_id: generatingArchive.archive_id,
              exam_duration: manualPaperForm.exam_duration,
              paper_desc: manualPaperForm.paper_desc,
            },
            user.user_id
          );

          if (res.code === 200) {
            showToast('success', '试卷创建成功');
            setShowGeneratePaperModal(false);
            const papersRes = await api.getExamPapers({ page: 1, page_size: 100, teacher_id: (user?.role_id === 8 || user?.role_name === "超级管理员" ? undefined : user?.user_id) });
            if (papersRes.code === 200 && papersRes.data) {
              setPapers(papersRes.data);
            }
          } else {
            showToast('error', `创建失败: ${res.message || '未知错误'}`);
          }
        } catch (error) {
          console.error('创建试卷失败:', error);
          showToast('error', '创建失败: ' + getErrorMessage(error));
        } finally {
          setIsGenerating(false);
        }
      }
    } catch (error) {
      console.error('生成试卷失败:', error);
      showToast('error', generateMode === 'ai' ? 'AI生成失败: ' + getErrorMessage(error) : '创建失败: ' + getErrorMessage(error));
    }
  };

  // 打开查看知识管理文件弹窗
  const openViewArchiveFilesModal = (archive: KnowledgeArchive) => {
    setSelectedArchiveForFiles(archive);
    setIsViewFilesModalOpen(true);
  };

  // 打开试卷查看弹窗
  const openPaperViewer = async (paperId: number) => {
    try {
      const res = await api.getExamPaper(paperId);
      if (res.code === 200 && res.data) {
        setViewingPaper(res.data);
        setIsPaperViewerOpen(true);
      }
    } catch (error) {
      showToast('error', '加载试卷失败');
    }
  };

  // 打开试卷编辑弹窗
  const openPaperEditor = async (paperId: number) => {
    try {
      const res = await api.getExamPaper(paperId);
      if (res.code === 200 && res.data) {
        setEditingPaper(res.data);
        setIsPaperEditorOpen(true);
      }
    } catch (error) {
      showToast('error', '加载试卷失败');
    }
  };

  // 删除试卷
  const handleDeletePaper = async (paper: ExamPaper) => {
    const confirmed = await confirmDialog.confirm({
      title: '确认删除',
      message: `确定要删除试卷"${paper.paper_name}"吗？此操作不可恢复。`,
      confirmText: '确认删除',
      danger: true,
    });
    if (!confirmed) return;

    try {
      const res = await api.deleteExamPaper(paper.paper_id);
      if (res.code === 200) {
        showToast('success', '删除成功');
        loadData();
      }
    } catch (error) {
      showToast('error', '删除失败');
    }
  };

  // 分配任务
  const handleAssign = async () => {
    // 验证选择 - 档案（学习任务）与试卷（考试任务）均可独立分配，可同时分配
    const errors: Record<string, string> = {};
    if (selectedArchiveIds.length === 0 && examTaskForm.paper_id === 0) {
      errors.item = '请选择至少一个档案或试卷';
    }
    if (selectedStudentIds.length === 0) {
      errors.students = '请选择至少一名用户';
    }
    if (!taskName.trim()) {
      errors.taskName = '请输入任务名称';
    }

    // 仅当选中档案时校验学习任务配置
    if (selectedArchiveIds.length > 0 && !learningTaskForm.deadline) {
      errors.deadline = '请设置截止时间';
    }
    // 仅当选中试卷时校验考试任务配置
    if (examTaskForm.paper_id > 0) {
      if (!examTaskForm.exam_start_time) {
        errors.examStart = '请设置考试开始时间';
      }
      if (!examTaskForm.exam_end_time) {
        errors.examEnd = '请设置考试结束时间';
      }
    }

    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      showToast('warning', '请填写所有必填字段');
      return;
    }

    setValidationErrors({});

    setAssigning(true);
    try {
      const results: string[] = [];
      let learningCount = 0;
      let examCount = 0;

      // 分配学习任务（选中了档案）
      if (selectedArchiveIds.length > 0) {
        // 收集所有选中的档案和文件
        const selectedArchives: KnowledgeArchive[] = [];
        const selectedFiles: { file_id: number; archive_id: number; file_name: string; archive_name: string }[] = [];

        for (const archiveId of selectedArchiveIds) {
          const archive = archives.find(a => a.archive_id === archiveId);
          if (archive) {
            selectedArchives.push(archive);
            // 获取该档案下选中的文件
            const archiveSelectedFiles = archive.files?.filter(f => selectedFileIds.includes(f.file_id)) || [];
            if (archiveSelectedFiles.length > 0) {
              // 如果选中了特定文件，使用选中的文件
              for (const file of archiveSelectedFiles) {
                selectedFiles.push({
                  file_id: file.file_id,
                  archive_id: archive.archive_id,
                  file_name: file.file_name,
                  archive_name: archive.archive_name,
                });
              }
            } else {
              // 如果没有选中特定文件，使用该档案的所有文件
              for (const file of archive.files || []) {
                selectedFiles.push({
                  file_id: file.file_id,
                  archive_id: archive.archive_id,
                  file_name: file.file_name,
                  archive_name: archive.archive_name,
                });
              }
            }
          }
        }

        if (selectedFiles.length === 0) {
          showToast('warning', '请至少选择一个文件');
          setAssigning(false);
          return;
        }

        // 创建单个学习任务，包含所有档案的文件
        const learningData: LearningTaskCreate = {
          learning_task_name: taskName,
          archive_id: selectedArchives[0]?.archive_id, // 使用第一个档案作为主档案
          archive_ids: selectedArchives.map(a => a.archive_id),
          deadline: learningTaskForm.deadline,
          task_desc: learningTaskForm.task_desc,
          student_ids: selectedStudentIds,
          file_ids: selectedFiles.map(f => ({ file_id: f.file_id, archive_id: f.archive_id })),
        };

        console.log('[任务分配] 合并任务数据:', learningData);

        const res = await api.createLearningTask(learningData, user!.user_id);
        console.log('[任务分配] 响应:', res);

        if (res.code === 200) {
          learningCount += selectedStudentIds.length;
        } else {
          results.push(`学习任务: ${res.message}`);
        }
      }

      // 分配考试任务（选中了试卷）
      if (examTaskForm.paper_id > 0) {
        // 创建考试任务
        const examData: ExamTaskCreate = {
          task_name: taskName,
          paper_id: examTaskForm.paper_id,
          student_ids: selectedStudentIds,
          exam_start_time: examTaskForm.exam_start_time,
          exam_end_time: examTaskForm.exam_end_time,
          task_desc: examTaskForm.task_desc || undefined,
        };

        const res = await api.createExamTask(examData, user!.user_id);
        if (res.code === 200) {
          examCount += selectedStudentIds.length;
        } else {
          results.push(`考试任务: ${res.message}`);
        }
      }

      if (results.length === 0) {
        // 组装成功提示（分别计数，区分只建一种或同时建两种）
        const parts: string[] = [];
        if (learningCount > 0) parts.push(`学习任务 ${learningCount} 个`);
        if (examCount > 0) parts.push(`考试任务 ${examCount} 个`);
        showToast('success', `分配成功！创建${parts.join('、')}`);
        // 清空表单
        setTaskName(''); // 清空任务名称，以便下次自动生成
        setLearningTaskForm({ deadline: '', task_desc: '' });
        setExamTaskForm({ paper_id: 0, exam_start_time: '', exam_end_time: '', task_desc: '' });
        setSelectedArchiveIds([]);
        setSelectedStudentIds([]);
        setSelectedFileIds([]); // 清空选中的文件
        setExpandedArchives([]); // 收起所有展开的文件列表
      } else {
        showToast('warning', `部分任务分配失败：${results.join(', ')}`);
      }
    } catch (error) {
      console.error('分配任务失败:', error);
      const errorMessage = getErrorMessage(error);
      showToast('error', `分配任务失败: ${errorMessage}`);
    } finally {
      setAssigning(false);
    }
  };

  // 查看任务详情 - 加载文件信息
  const handleViewTaskDetail = async (taskData: AssignedTaskOverview) => {
    setSelectedTaskDetail(taskData);
    setIsTaskDetailOpen(true);

    // 清空之前的文件信息
    setViewDetailFiles([]);

    // 仅学习任务加载文件信息
    if (taskData.task_type === 'learning') {
      setViewDetailFilesLoading(true);
      try {
        const filesRes = await api.getLearningTaskFiles(taskData.task_id);
        if (filesRes.code === 200 && filesRes.data) {
          setViewDetailFiles(filesRes.data);
        }
      } catch (error) {
        console.error('加载任务文件信息失败:', error);
      } finally {
        setViewDetailFilesLoading(false);
      }
    }
  };

  // 试卷管理相关辅助函数
  // 编辑任务
  const handleEditTask = async (taskData: AssignedTaskOverview) => {
    setEditingTask(taskData);

    // 清空文件信息
    setEditTaskFiles([]);

    if (taskData.task_type === 'learning') {
      const task = taskData.task_info as LearningTask;
      setEditTaskForm({
        task_name: task.learning_task_name || taskData.task_name,
        deadline: task.deadline ? new Date(task.deadline).toISOString().slice(0, 16) : '',
        exam_start_time: '',
        exam_end_time: '',
        task_desc: task.task_desc || '',
      });

      // 加载学习任务的文件信息（使用新的教师专用API）
      setFilesLoading(true);
      try {
        const filesRes = await api.getLearningTaskFiles(taskData.task_id);
        if (filesRes.code === 200 && filesRes.data) {
          setEditTaskFiles(filesRes.data);
        }
      } catch (error) {
        console.error('加载任务文件信息失败:', error);
      } finally {
        setFilesLoading(false);
      }
    } else {
      const task = taskData.task_info as ExamTask;
      setEditTaskForm({
        task_name: task.task_name || taskData.task_name,
        deadline: '',
        exam_start_time: task.exam_start_time ? new Date(task.exam_start_time).toISOString().slice(0, 16) : '',
        exam_end_time: task.exam_end_time ? new Date(task.exam_end_time).toISOString().slice(0, 16) : '',
        task_desc: task.task_desc || '',
      });
    }

    // 加载已分配的学生和可用学生
    setStudentsLoading(true);
    try {
      // 根据任务类型获取已分配的学生
      let assignedStudentsData: AssignedStudent[] = [];
      if (taskData.task_type === 'learning') {
        const res = await api.getLearningTaskStudents(taskData.task_id);
        if (res.code === 200 && res.data) {
          assignedStudentsData = res.data;
        }
      } else {
        const res = await api.getExamTaskStudents(taskData.task_id);
        if (res.code === 200 && res.data) {
          assignedStudentsData = res.data;
        }
      }

      // 获取已分配学生的详细信息（包括角色和部门）
      const detailedAssignedStudents = await Promise.all(
        assignedStudentsData.map(async (student) => {
          try {
            const userDetailRes = await api.getUserByAccount(student.account);
            if (userDetailRes.code === 200 && userDetailRes.data) {
              return {
                ...student,
                role_name: userDetailRes.data.role_name,
                department: userDetailRes.data.dept_name || '未分配',
              };
            }
          } catch (error) {
            console.warn(`获取用户 ${student.account} 详细信息失败:`, error);
          }

          // 如果获取详细信息失败，返回基本数据
          return {
            ...student,
            role_name: '学生',
            department: '未分配',
          };
        })
      );
      setAssignedStudents(detailedAssignedStudents);

      // 获取所有可用用户（排除已分配的用户）
      const usersRes = await api.getAssignableUsers({ page: 1, page_size: 100 });
      if (usersRes.code === 200 && usersRes.data) {
        const assignedUserIds = new Set(assignedStudentsData.map(s => s.user_id));
        const available = usersRes.data.filter(s => !assignedUserIds.has(s.user_id));
        setAvailableStudents(available);
      }
    } catch (error) {
      console.error('加载用户列表失败:', error);
      showToast('error', '加载用户列表失败');
    } finally {
      setStudentsLoading(false);
    }

    setIsEditTaskOpen(true);
  };

  // 保存编辑的任务（包括学生分配变更）
  const handleSaveTaskEdit = async () => {
    if (!editingTask) return;

    if (!editTaskForm.task_name.trim()) {
      showToast('warning', '请输入任务名称');
      return;
    }

    setSavingTask(true);
    try {
      // 保存任务基本信息
      if (editingTask.task_type === 'learning') {
        if (!editTaskForm.deadline) {
          showToast('warning', '请设置截止时间');
          setSavingTask(false);
          return;
        }
        const data: LearningTaskUpdate = {
          learning_task_name: editTaskForm.task_name.trim(),
          deadline: editTaskForm.deadline,
          task_desc: editTaskForm.task_desc.trim() || undefined,
        };
        const res = await api.updateLearningTask(editingTask.task_id, data);
        if (res.code !== 200) {
          showToast('error', res.message || '更新失败');
          setSavingTask(false);
          return;
        }

        // 更新文件的需学习时长设置 - 只对文档类型文件更新
        const updatePromises = editTaskFiles.map(async (file) => {
          if (isDocumentFileType(file.file_type)) {
            return api.updateFileRequiredDuration(editingTask.task_id, file.file_id, file.required_duration || 60, user!.user_id);
          } else {
            return Promise.resolve({ code: 200, data: {}, message: 'Success' });
          }
        });
        try {
          const results = await Promise.allSettled(updatePromises);
          const failedUpdates = results.filter(result => result.status === 'rejected');
          if (failedUpdates.length > 0) {
            console.error('部分文件时长更新失败:', failedUpdates);
            showToast('warning', '任务已更新，但部分文件时长设置失败');
          }
        } catch (updateError) {
          console.error('更新文件时长设置失败:', updateError);
          showToast('warning', '任务已更新，但部分文件时长设置失败');
        }
      } else {
        if (!editTaskForm.exam_start_time || !editTaskForm.exam_end_time) {
          showToast('warning', '请设置考试时间');
          setSavingTask(false);
          return;
        }
        const data: ExamTaskUpdate = {
          task_name: editTaskForm.task_name.trim(),
          exam_start_time: editTaskForm.exam_start_time,
          exam_end_time: editTaskForm.exam_end_time,
          task_desc: editTaskForm.task_desc.trim() || undefined,
        };
        const res = await api.updateExamTask(editingTask.task_id, data);
        if (res.code !== 200) {
          showToast('error', res.message || '更新失败');
          setSavingTask(false);
          return;
        }
      }

      // 保存学生分配变更
      const studentIds = assignedStudents.map(s => s.user_id);
      if (editingTask.task_type === 'learning') {
        const assignRes = await api.assignLearningTask(editingTask.task_id, studentIds);
        if (assignRes.code !== 200) {
          showToast('warning', '任务信息已更新，但学生分配保存失败');
        }
      } else {
        const assignRes = await api.assignExamTask(editingTask.task_id, studentIds);
        if (assignRes.code !== 200) {
          showToast('warning', '任务信息已更新，但学生分配保存失败');
        }
      }

      showToast('success', editingTask.task_type === 'learning' ? '学习任务更新成功' : '考试任务更新成功');
      setIsEditTaskOpen(false);
      loadAssignedTasks();
    } catch (error) {
      console.error('更新任务失败:', error);
      showToast('error', '更新任务失败');
    } finally {
      setSavingTask(false);
    }
  };

  // 添加用户到已分配列表（本地状态操作，不调用API）
  const handleAddStudents = () => {
    if (selectedToAdd.length === 0) return;

    // 将选中的用户从"可添加"移到"已分配"
    const studentsToAdd = availableStudents.filter(s => selectedToAdd.includes(s.user_id));
    const newAssignedStudents = [
      ...assignedStudents,
      ...studentsToAdd.map(s => ({
        user_id: s.user_id,
        account: s.account,
        user_name: s.user_name,
        assign_time: '', // 拟执行状态，无实际分配时间
        role_name: s.role_name,
        department: s.dept_name || '未分配',
      })),
    ];
    const newAvailableStudents = availableStudents.filter(s => !selectedToAdd.includes(s.user_id));

    setAssignedStudents(newAssignedStudents);
    setAvailableStudents(newAvailableStudents);
    setSelectedToAdd([]);
  };

  // 从已分配列表移除用户（本地状态操作，不调用API）
  const handleRemoveStudents = () => {
    if (selectedToRemove.length === 0) return;

    // 将选中的用户从"已分配"移回"可添加"
    const studentsToRemove = assignedStudents.filter(s => selectedToRemove.includes(s.user_id));
    const newAvailableStudents = [
      ...availableStudents,
      ...studentsToRemove.map(s => ({
        user_id: s.user_id,
        user_name: s.user_name,
        account: s.account,
        role_id: 0, // 拟执行状态，role_id暂设为0
        role_name: s.role_name || '学生',
        status: 1,
        dept_name: s.department || '未分配',
      })),
    ];
    const newAssignedStudents = assignedStudents.filter(s => !selectedToRemove.includes(s.user_id));

    setAssignedStudents(newAssignedStudents);
    setAvailableStudents(newAvailableStudents);
    setSelectedToRemove([]);
  };

  
  // 删除任务
  const handleDeleteTask = async (taskData: AssignedTaskOverview) => {
    const confirmed = await confirmDialog.confirm({
      title: '确认删除',
      message: `确定要删除任务"${taskData.task_name}"吗？此操作不可恢复。`,
      confirmText: '确认删除',
      danger: true,
    });
    if (!confirmed) return;

    try {
      if (taskData.task_type === 'learning') {
        await api.deleteLearningTask(taskData.task_id);
      } else {
        await api.deleteExamTask(taskData.task_id);
      }
      showToast('success', '删除成功');
      loadAssignedTasks();
    } catch (error) {
      showToast('error', '删除失败');
    }
  };

  const togglePaperSelection = (paperId: number) => {
    setSelectedPapers(prev =>
      prev.includes(paperId)
        ? prev.filter(id => id !== paperId)
        : [...prev, paperId]
    );
  };

  const selectAllPapers = () => {
    if (selectedPapers.length === filteredPapers.length) {
      setSelectedPapers([]);
    } else {
      setSelectedPapers(filteredPapers.map(p => p.paper_id));
    }
  };

  const openBatchDeleteModal = () => {
    setIsBatchDeleteModalOpen(true);
  };

  const closeBatchDeleteModal = () => {
    setIsBatchDeleteModalOpen(false);
  };

  const handleBatchDelete = async () => {
    try {
      if (selectedPapers.length === 0) {
        showToast('warning', '请至少选择一个试卷');
        return;
      }

      for (const paperId of selectedPapers) {
        await api.deleteExamPaper(paperId);
      }
      setSelectedPapers([]);
      await loadPapers();
      showToast('success', `成功删除 ${selectedPapers.length} 份试卷`);
    } catch (error) {
      showToast('error', `批量删除失败: ${error}`);
    } finally {
      closeBatchDeleteModal();
    }
  };

  const handleCreatePaper = async () => {
    if (!paperForm.paper_name.trim()) {
      showToast('warning', '请输入试卷名称');
      return;
    }

    setSavingPaper(true);
    try {
      const res = await api.createExamPaper(
        {
          paper_name: paperForm.paper_name.trim(),
          paper_desc: paperForm.paper_desc.trim() || undefined,
          total_score: paperForm.total_score,
          pass_score: paperForm.pass_score,
          exam_duration: paperForm.exam_duration,
          archive_id: 0,
        },
        user?.user_id || 1
      );
      if (res.code === 200) {
        showToast('success', '试卷创建成功');
        closePaperModal();
        loadPapers();
      }
    } catch (error) {
      showToast('error', '创建试卷失败');
    } finally {
      setSavingPaper(false);
    }
  };

  const closePaperModal = () => {
    setIsPaperModalOpen(false);
  };

  const openPaperModal = () => {
    setPaperForm({
      paper_name: '',
      paper_desc: '',
      total_score: 100,
      pass_score: 60,
      exam_duration: 60,
    });
    setIsPaperModalOpen(true);
  };

  const [isPaperModalOpen, setIsPaperModalOpen] = useState(false);

  // 加载试卷列表
  const loadPapers = async () => {
    setPapersLoading(true);
    try {
      const res = await api.getExamPapers({ page: 1, page_size: 100, teacher_id: (user?.role_id === 8 || user?.role_name === "超级管理员" ? undefined : user?.user_id) });
      if (res.code === 200 && res.data) {
        setPapers(res.data);
      }
    } catch (error) {
      console.error('加载试卷失败:', error);
      showToast('error', '加载试卷列表失败');
    } finally {
      setPapersLoading(false);
    }
  };

  // 保存试卷编辑
  const handleSavePaper = (paper: ExamPaperWithQuestions) => {
    showToast('success', '试卷保存成功');
    setIsPaperEditorOpen(false);
    setEditingPaper(null);
    loadPapers();
  };

  // 筛选试卷
  const filteredPapers = papers.filter((paper) =>
    paper.paper_name.toLowerCase().includes(paperSearch.toLowerCase())
  );

  // 试卷分页计算
  const indexOfLastPaper = currentPaperPage * papersPerPage;
  const indexOfFirstPaper = indexOfLastPaper - papersPerPage;
  const currentPapers = filteredPapers.slice(indexOfFirstPaper, indexOfLastPaper);
  const totalPaperPages = Math.ceil(filteredPapers.length / papersPerPage);

  const formatDateTime = (dateStr: string) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleString('zh-CN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  };

  // 简化的日期时间格式，用于截止时间显示
  const formatShortDateTime = (dateStr: string) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);

    // 获取月、日、小时和分钟
    const month = date.getMonth() + 1; // getMonth() 返回 0-11，需要加 1
    const day = date.getDate();
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');

    return `${month}月${day}日 ${hours}:${minutes}`;
  };

  if (loading) {
    return (
      <div className={styles.loading}>
        <motion.div
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.3 }}
        >
          <Loader2 size={40} className={styles.spinner} />
        </motion.div>
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
        >
          加载中...
        </motion.p>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      {/* 页头 */}
      <div className={styles.header}>
        <div className={styles.headerContent}>
          <h1 className={styles.title}>任务管理</h1>
          <p className={styles.subtitle}>选择知识管理和学生，分配学习与考试任务</p>
        </div>
      </div>

      {/* 标签页切换 */}
      <div className={styles.tabContainer}>
        <button
          className={`${styles.tabBtn} ${activeTab === 'overview' ? styles.activeTab : ''}`}
          onClick={() => setActiveTab('overview')}
        >
          <BarChart3 size={16} />
          任务总览
        </button>
        <button
          className={`${styles.tabBtn} ${activeTab === 'allocation' ? styles.activeTab : ''}`}
          onClick={() => setActiveTab('allocation')}
        >
          <ClipboardCheck size={16} />
          任务分配
        </button>
        <button
          className={`${styles.tabBtn} ${activeTab === 'papers' ? styles.activeTab : ''}`}
          onClick={() => setActiveTab('papers')}
        >
          <FileEdit size={16} />
          试卷管理
        </button>
      </div>

      {/* 三栏布局或任务总览或试卷管理 */}
      {activeTab === 'allocation' ? (
        <div className={styles.threeColumnLayout}>
          {/* 左栏 - 事项（知识管理/试卷） */}
          <div className={styles.column}>
            <div className={styles.columnHeader}>
              <div className={styles.columnTitle}>
                {itemType === 'archive' ? <BookOpen size={18} /> : <FileEdit size={18} />}
                <span>事项</span>
              </div>
              {/* 事项类型切换按钮（切换查看列表；档案与试卷可同时选中） */}
              <div className={styles.itemTypeToggle}>
                <button
                  className={`${styles.itemTypeBtn} ${itemType === 'archive' ? styles.activeItemType : ''}`}
                  onClick={() => toggleItemType('archive')}
                  title="知识管理"
                >
                  <BookOpen size={14} />
                  档案
                  {selectedArchiveIds.length > 0 && ` (${selectedArchiveIds.length})`}
                </button>
                <button
                  className={`${styles.itemTypeBtn} ${itemType === 'paper' ? styles.activeItemType : ''}`}
                  onClick={() => toggleItemType('paper')}
                  title="试卷"
                >
                  <FileEdit size={14} />
                  试卷
                  {examTaskForm.paper_id > 0 && ' (1)'}
                </button>
              </div>
              <span className={styles.columnBadge}>
                {selectedArchiveIds.length + (examTaskForm.paper_id > 0 ? 1 : 0)}
              </span>
            </div>
            {/* 档案可见性说明 */}
            {itemType === 'archive' && (
              <div className={styles.columnHint}>
                {(user?.role_id === 8 || user?.role_name === '超级管理员' || user?.role_name === '考官')
                  ? '展示部门可见和公开档案'
                  : '展示部门可见和公开档案'
                }
              </div>
            )}
            {(validationErrors.archive || validationErrors.paper || validationErrors.item) && (
              <div className={`${styles.errorText} ${styles.fieldHint}`}>
                {validationErrors.archive || validationErrors.paper || validationErrors.item}
              </div>
            )}
            <div className={styles.searchBox}>
              <div className={styles.searchInput}>
                <Search size={16} />
                <input
                  type="text"
                  placeholder={itemType === 'archive' ? "搜索档案..." : "搜索试卷..."}
                  value={itemType === 'archive' ? archiveSearch : paperSearch}
                  onChange={(e) => itemType === 'archive' ? setArchiveSearch(e.target.value) : setPaperSearch(e.target.value)}
                />
              </div>
            </div>
            <div className={styles.columnContent}>
              {itemType === 'archive' ? (
                // 知识管理列表
                filteredArchives.length === 0 ? (
                  <div className={styles.emptyState}>
                    <BookOpen size={32} />
                    <p>暂无知识管理</p>
                  </div>
                ) : (
                  filteredArchives.map((archive) => {
                    const isExpanded = expandedArchives.includes(archive.archive_id);
                    const selectedFileCount = getSelectedFileCountForArchive(archive.archive_id);
                    return (
                      <div key={archive.archive_id}>
                        <motion.div
                          className={`${styles.listItem} ${
                            selectedArchiveIds.includes(archive.archive_id) ? styles.selected : ''
                          }`}
                          onClick={() => toggleArchiveSelection(archive.archive_id)}
                          whileHover={{ scale: 1.01 }}
                        >
                          <div className={styles.itemIcon}>
                            <BookOpen size={16} />
                          </div>
                          <div className={styles.itemInfo}>
                            <div className={styles.itemName}>{archive.archive_name}</div>
                            <div className={styles.itemMeta}>
                              <span className={styles.itemTag}>
                                <FileText size={12} />
                                {archive.files?.length || 0} 文件
                                {selectedFileCount > 0 && ` (${selectedFileCount} 已选)`}
                              </span>
                            </div>
                          </div>
                          <div className={styles.itemActions}>
                            <button
                              className={styles.smallBtn}
                              onClick={(e) => toggleArchiveExpand(archive.archive_id, e)}
                              title={isExpanded ? '收起文件' : '展开文件'}
                            >
                              {isExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                            </button>
                            <div className={styles.itemCheck}>
                              {selectedArchiveIds.includes(archive.archive_id) && <Check size={14} />}
                            </div>
                          </div>
                        </motion.div>
                        {/* 展开的文件列表 */}
                        {isExpanded && archive.files && archive.files.length > 0 && (
                          <motion.div
                            className={styles.fileListContainer}
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: 'auto' }}
                            exit={{ opacity: 0, height: 0 }}
                          >
                            {archive.files.map((file) => (
                              <div
                                key={file.file_id}
                                className={`${styles.fileItem} ${
                                  selectedFileIds.includes(file.file_id) ? styles.fileItemSelected : ''
                                }`}
                                onClick={(e) => toggleFileSelection(file.file_id, archive.archive_id, e)}
                              >
                                <div className={styles.fileIcon}>
                                  <FileText size={14} />
                                </div>
                                <div className={styles.fileInfo}>
                                  <div className={styles.fileName}>{file.file_name}</div>
                                  <div className={styles.fileMeta}>
                                    <span>{file.file_type?.toUpperCase()}</span>
                                  </div>
                                </div>
                                <div className={styles.fileCheck}>
                                  {selectedFileIds.includes(file.file_id) && <Check size={12} />}
                                </div>
                              </div>
                            ))}
                          </motion.div>
                        )}
                      </div>
                    );
                  })
                )
              ) : (
                // 试卷列表
                filteredPapers.length === 0 ? (
                  <div className={styles.emptyState}>
                    <FileEdit size={32} />
                    <p>暂无试卷</p>
                  </div>
                ) : (
                  filteredPapers.map((paper) => (
                    <motion.div
                      key={paper.paper_id}
                      className={`${styles.listItem} ${
                        examTaskForm.paper_id === paper.paper_id ? styles.selected : ''
                      }`}
                      onClick={() => {
                        // 单选切换：点击已选中的试卷则取消选中（便于单独取消考试任务）
                        setExamTaskForm(prev => ({
                          ...prev,
                          paper_id: prev.paper_id === paper.paper_id ? 0 : paper.paper_id,
                        }));
                        setValidationErrors(prev => { const next = { ...prev }; delete next.paper; return next; });
                      }}
                      whileHover={{ scale: 1.01 }}
                    >
                      <div className={styles.itemIcon}>
                        <FileEdit size={16} />
                      </div>
                      <div className={styles.itemInfo}>
                        <div className={styles.itemName}>{paper.paper_name}</div>
                        <div className={styles.itemMeta}>
                          <span className={styles.itemTag}>
                            <BarChart3 size={12} />
                            {paper.total_score}分
                          </span>
                          <span className={styles.itemTag}>
                            <Clock size={12} />
                            {paper.exam_duration}分钟
                          </span>
                        </div>
                      </div>
                      <div className={styles.itemActions}>
                        <button
                          className={styles.smallBtn}
                          onClick={(e) => {
                            e.stopPropagation();
                            // 只显示试卷基本信息，不显示题目
                            setViewingPaperInfo(paper);
                            setIsPaperInfoOpen(true);
                          }}
                          title="查看试卷信息"
                        >
                          <Eye size={14} />
                        </button>
                        <div className={styles.itemCheck}>
                          {examTaskForm.paper_id === paper.paper_id && <Check size={14} />}
                        </div>
                      </div>
                    </motion.div>
                  ))
                )
              )}
            </div>
          </div>

          {/* 中栏 - 用户列表 */}
          <div className={styles.column}>
            <div className={styles.columnHeader}>
              <div className={styles.columnTitle}>
                <Users size={18} />
                <span>用户列表</span>
              </div>
              <span className={styles.columnBadge}>
                {selectedStudentIds.length}
              </span>
            </div>
            {validationErrors.students && (
              <div className={`${styles.errorText} ${styles.fieldHint}`} style={{ padding: '0 var(--space-3)' }}>
                {validationErrors.students}
              </div>
            )}
            <div className={styles.searchBox}>
              <div className={styles.searchInput}>
                <Search size={16} />
                <input
                  type="text"
                  placeholder="搜索用户/角色/部门"
                  value={studentSearch}
                  onChange={(e) => setStudentSearch(e.target.value)}
                />
              </div>
            </div>
            {filteredStudents.length > 0 && (
              <div className={styles.selectButtons} style={{ padding: '0 var(--space-3) var(--space-2)' }}>
                <button
                  className={styles.selectAllBtn}
                  onClick={selectAllStudents}
                  disabled={filteredStudents.length === 0}
                >
                  {selectedStudentIds.length === filteredStudents.length ? '取消全选' : '全选'}
                </button>
                <button
                  className={styles.clearBtn}
                  onClick={() => setSelectedStudentIds([])}
                  disabled={selectedStudentIds.length === 0}
                >
                  清空
                </button>
              </div>
            )}
            <div className={styles.columnContent}>
              {filteredStudents.length === 0 ? (
                <div className={styles.emptyState}>
                  <Users size={32} />
                  <p>暂无学生</p>
                </div>
              ) : (
                filteredStudents.map((student) => {
                  const isSelected = selectedStudentIds.includes(student.user_id);

                  return (
                    <motion.div
                      key={student.user_id}
                      className={`${styles.listItem} ${isSelected ? styles.selected : ''}`}
                      onClick={() => toggleStudentSelection(student.user_id)}
                      whileHover={{ scale: 1.01 }}
                    >
                      <div className={styles.itemAvatar}>
                        {student.user_name.charAt(0)}
                      </div>
                      <div className={styles.itemInfo}>
                        <div className={styles.itemName}>{student.user_name}</div>
                        <div className={styles.itemMeta}>
                          <span className={styles.itemTag}>{student.account}</span>
                          <span className={styles.itemTag}>{student.role_name}</span>
                          {student.dept_name && <span className={styles.itemTag}>{student.dept_name}</span>}
                        </div>
                      </div>
                      <div className={styles.itemCheck}>
                        {isSelected && <Check size={14} />}
                      </div>
                    </motion.div>
                  );
                })
              )}
            </div>
          </div>

          {/* 右栏 - 任务分配 */}
          <div className={styles.column}>
            <div className={styles.columnHeader}>
              <div className={styles.columnTitle}>
                <ClipboardCheck size={18} />
                <span>任务分配</span>
              </div>
            </div>
            <div className={styles.columnContent}>
              <div className={styles.assignmentPanel}>
                {/* 选择信息 */}
                <div className={styles.assignmentInfo}>
                  {selectedArchiveIds.length === 0 && examTaskForm.paper_id === 0 ? (
                    <p>
                      请在左侧选择 <strong>档案</strong> 或 <strong>试卷</strong>，并勾选用户后分配任务
                    </p>
                  ) : (
                    <p>
                      {selectedArchiveIds.length > 0 && (
                        <span>
                          已选 <strong>{selectedArchiveIds.length}</strong> 个档案
                          {selectedFileIds.length > 0 && (
                            <span>（<strong>{selectedFileIds.length}</strong> 个文件）</span>
                          )}
                          {'　'}
                        </span>
                      )}
                      {examTaskForm.paper_id > 0 && (
                        <span>
                          试卷：<strong>{papers.find(p => p.paper_id === examTaskForm.paper_id)?.paper_name || '已选择'}</strong>
                          {'　'}
                        </span>
                      )}
                      ，<strong>{selectedStudentIds.length}</strong> 名用户
                    </p>
                  )}
                </div>

                {/* 任务名称输入 */}
                <div className={styles.formSection}>
                  <div className={styles.formTitle}>任务名称 <span style={{ color: '#ef4444' }}>*</span></div>
                  <div className={styles.formField}>
                    <label className={styles.formLabel}>请输入任务名称（必填）</label>
                    <input
                      type="text"
                      className={`${styles.formInput} ${validationErrors.taskName ? styles.inputError : ''}`}
                      placeholder="例如：《道德经》第一章学习任务"
                      value={taskName}
                      onChange={(e) => {
                        setTaskName(e.target.value);
                        setValidationErrors(prev => { const next = {...prev}; delete next.taskName; return next; });
                      }}
                    />
                    {validationErrors.taskName && <div className={`${styles.errorText} ${styles.fieldHint}`}>{validationErrors.taskName}</div>}
                  </div>
                </div>

                {/* 根据实际选择显示学习/考试配置；同时选中档案和试卷时两个配置区并列显示 */}
                {selectedArchiveIds.length > 0 && (
                  // 学习任务配置
                  <div className={styles.formSection}>
                    <div className={styles.formTitle}>学习任务配置</div>
                    <div className={styles.formField}>
                      <label className={styles.formLabel}>截止时间 <span style={{ color: '#ef4444' }}>*</span></label>
                      <input
                        type="datetime-local"
                        className={`${styles.formInput} ${validationErrors.deadline ? styles.inputError : ''}`}
                        value={learningTaskForm.deadline}
                        onChange={(e) => {
                          setLearningTaskForm((prev) => ({
                            ...prev,
                            deadline: e.target.value,
                          }));
                          setValidationErrors(prev => { const next = {...prev}; delete next.deadline; return next; });
                        }}
                      />
                      {validationErrors.deadline && <div className={`${styles.errorText} ${styles.fieldHint}`}>{validationErrors.deadline}</div>}
                    </div>
                    <div className={styles.formField}>
                      <label className={styles.formLabel}>任务描述</label>
                      <input
                        type="text"
                        className={styles.formInput}
                        placeholder="可选"
                        value={learningTaskForm.task_desc}
                        onChange={(e) =>
                          setLearningTaskForm((prev) => ({
                            ...prev,
                            task_desc: e.target.value,
                          }))
                        }
                      />
                    </div>
                  </div>
                )}

                {examTaskForm.paper_id > 0 && (
                  // 考试任务配置
                  <div className={styles.formSection}>
                    <div className={styles.formTitle}>考试任务配置</div>
                    <div className={styles.formField}>
                      <label className={styles.formLabel}>考试开始时间 <span style={{ color: '#ef4444' }}>*</span></label>
                      <input
                        type="datetime-local"
                        className={`${styles.formInput} ${validationErrors.examStart ? styles.inputError : ''}`}
                        value={examTaskForm.exam_start_time}
                        onChange={(e) => {
                          setExamTaskForm((prev) => ({
                            ...prev,
                            exam_start_time: e.target.value,
                          }));
                          setValidationErrors(prev => { const next = {...prev}; delete next.examStart; return next; });
                        }}
                      />
                      {validationErrors.examStart && <div className={`${styles.errorText} ${styles.fieldHint}`}>{validationErrors.examStart}</div>}
                    </div>
                    <div className={styles.formField}>
                      <label className={styles.formLabel}>考试结束时间 <span style={{ color: '#ef4444' }}>*</span></label>
                      <input
                        type="datetime-local"
                        className={`${styles.formInput} ${validationErrors.examEnd ? styles.inputError : ''}`}
                        value={examTaskForm.exam_end_time}
                        onChange={(e) => {
                          setExamTaskForm((prev) => ({
                            ...prev,
                            exam_end_time: e.target.value,
                          }));
                          setValidationErrors(prev => { const next = {...prev}; delete next.examEnd; return next; });
                        }}
                      />
                      {validationErrors.examEnd && <div className={`${styles.errorText} ${styles.fieldHint}`}>{validationErrors.examEnd}</div>}
                    </div>
                    <div className={styles.formField}>
                      <label className={styles.formLabel}>任务描述</label>
                      <input
                        type="text"
                        className={styles.formInput}
                        placeholder="可选"
                        value={examTaskForm.task_desc}
                        onChange={(e) =>
                          setExamTaskForm((prev) => ({
                            ...prev,
                            task_desc: e.target.value,
                          }))
                        }
                      />
                    </div>
                  </div>
                )}

                {selectedArchiveIds.length === 0 && examTaskForm.paper_id === 0 && (
                  <div className={styles.formSection}>
                    <div className={styles.formTitle}>配置</div>
                    <div className={styles.assignmentInfo} style={{ background: 'transparent', padding: 0, border: 'none' }}>
                      请在左侧选择档案（学习任务）或试卷（考试任务）后填写对应配置
                    </div>
                  </div>
                )}

                {/* 确认分配按钮 */}
                {canManage && (
                  <button
                    className={styles.assignBtn}
                    onClick={handleAssign}
                    disabled={assigning}
                  >
                    {assigning ? (
                      <>
                        <Loader2 size={16} className={styles.spinner} />
                        分配中...
                      </>
                    ) : (
                      <>
                        <Check size={16} />
                        确认分配
                      </>
                    )}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      ) : activeTab === 'overview' ? (
        /* 任务总览 - 改为表格形式 */
        <div className={styles.overviewContainer}>
          {/* 筛选工具栏 - 方案D：标签式筛选 */}
          <div className={styles.filterToolbar}>
            {/* 搜索框 */}
            <div className={styles.searchInput}>
              <Search size={14} strokeWidth={1.5} />
              <input
                type="text"
                placeholder="搜索任务名称..."
                value={taskSearchFilter}
                onChange={(e) => setTaskSearchFilter(e.target.value)}
              />
            </div>

            {/* 任务类型标签组 */}
            <div className={styles.filterTags}>
              <span className={styles.filterTagLabel}>类型</span>
              <div className={styles.tagGroup}>
                <button
                  className={`${styles.filterTag} ${taskTypeFilter === 'all' ? styles.filterTagActive : ''}`}
                  onClick={() => setTaskTypeFilter('all')}
                >
                  全部
                </button>
                <button
                  className={`${styles.filterTag} ${taskTypeFilter === 'learning' ? styles.filterTagActive : ''}`}
                  onClick={() => setTaskTypeFilter('learning')}
                >
                  学习任务
                </button>
                <button
                  className={`${styles.filterTag} ${taskTypeFilter === 'exam' ? styles.filterTagActive : ''}`}
                  onClick={() => setTaskTypeFilter('exam')}
                >
                  考试任务
                </button>
              </div>
            </div>

            {/* 状态标签组 */}
            <div className={styles.filterTags}>
              <span className={styles.filterTagLabel}>状态</span>
              <div className={styles.tagGroup}>
                <button
                  className={`${styles.filterTag} ${taskStatusFilter === 'all' ? styles.filterTagActive : ''}`}
                  onClick={() => setTaskStatusFilter('all')}
                >
                  全部
                </button>
                <button
                  className={`${styles.filterTag} ${taskStatusFilter === 'ongoing' ? styles.filterTagActive : ''}`}
                  onClick={() => setTaskStatusFilter('ongoing')}
                >
                  进行中
                </button>
                <button
                  className={`${styles.filterTag} ${taskStatusFilter === 'pending' ? styles.filterTagActive : ''}`}
                  onClick={() => setTaskStatusFilter('pending')}
                >
                  未开始
                </button>
                <button
                  className={`${styles.filterTag} ${taskStatusFilter === 'ended' ? styles.filterTagActive : ''}`}
                  onClick={() => setTaskStatusFilter('ended')}
                >
                  已结束
                </button>
              </div>
            </div>

            {/* 统计信息 */}
            <div className={styles.filterStats}>
              <span>共 <strong>{sortedTasks.length}</strong> 条记录</span>
            </div>
          </div>

          {tasksLoading ? (
            <div className={styles.loading}>
              <motion.div
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ duration: 0.3 }}
              >
                <Loader2 size={40} className={styles.spinner} />
              </motion.div>
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.2 }}
              >
                加载中...
              </motion.p>
            </div>
          ) : filteredTasks.length === 0 ? (
            <div className={styles.emptyState}>
              <BarChart3 size={48} />
              <p>{assignedTasks.length === 0 ? '暂无已分配任务' : '没有符合筛选条件的任务'}</p>
            </div>
          ) : (
            <div className={styles.tableContainer}>
              <div className={styles.tableWrapper}>
                <table className={styles.table}>
                  <thead>
                    <tr>
                      <th>任务名称</th>
                      <th className={styles.centerCell}>任务类型</th>
                      <th className={styles.centerCell}>总人数</th>
                      <th className={styles.centerCell}>已完成</th>
                      <th className={styles.centerCell}>完成率</th>
                      <th className={styles.centerCell}>截止时间</th>
                      <th className={styles.centerCell}>状态</th>
                      <th className={styles.centerCell}>操作</th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentTasks.map((taskData, index) => {
                      const task = taskData.task_info;
                      const completedRate = Math.round((taskData.completed_count / taskData.total_students) * 100);
                      const status = taskData.task_type === 'learning'
                        ? getLearningTaskStatus((task as LearningTask).deadline)
                        : getTaskStatus((task as ExamTask).exam_start_time, (task as ExamTask).exam_end_time);

                      const statusConfig = status === 'ongoing'
                        ? { label: '进行中', className: styles.ongoing }
                        : status === 'pending'
                        ? { label: '未开始', className: styles.pending }
                        : { label: '已结束', className: styles.ended };

                      return (
                        <motion.tr
                          key={`${taskData.task_type}-${taskData.task_id}`}
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: index * 0.05 }}
                        >
                          <td data-label="任务名称">{taskData.task_name}</td>
                          <td className={styles.centerCell} data-label="任务类型">
                            <span className={`${styles.taskTypeTag} ${taskData.task_type === 'learning' ? styles.learningTag : styles.examTag}`}>
                              {taskData.task_type === 'learning' ? '学习任务' : '考试任务'}
                            </span>
                          </td>
                          <td className={styles.centerCell} data-label="总人数">{taskData.total_students}</td>
                          <td className={styles.centerCell} data-label="已完成">
                            {taskData.task_type === 'exam' && taskData.passed_count !== null
                              ? taskData.passed_count
                              : taskData.completed_count}
                          </td>
                          <td className={styles.centerCell} data-label="完成率">
                            <div className={styles.progressContainer}>
                              <div className={styles.progressBar}>
                                <div
                                  className={styles.progressBarFill}
                                  style={{ width: `${completedRate}%` }}
                                ></div>
                              </div>
                              <span className={styles.progressText}>{completedRate}%</span>
                            </div>
                          </td>
                          <td className={styles.centerCell} data-label="截止时间">
                            {taskData.task_type === 'learning'
                              ? formatShortDateTime((task as LearningTask).deadline)
                              : formatShortDateTime((task as ExamTask).exam_end_time)}
                          </td>
                          <td className={styles.centerCell} data-label="状态">
                            <span className={`${styles.status} ${statusConfig.className}`}>
                              {statusConfig.label}
                            </span>
                          </td>
                          <td className={styles.centerCell} data-label="操作">
                            <div className={styles.tableActions}>
                              <button
                                className={styles.tableActionBtn}
                                onClick={() => handleViewTaskDetail(taskData)}
                                title="查看详情"
                              >
                                <Eye size={18} />
                              </button>
                              {canManage && (
                                <>
                                  <button
                                    className={styles.tableActionBtn}
                                    onClick={() => handleEditTask(taskData)}
                                    title="编辑任务"
                                  >
                                    <Edit size={18} />
                                  </button>
                                  <button
                                    className={`${styles.tableActionBtn} ${styles.danger}`}
                                    onClick={() => handleDeleteTask(taskData)}
                                    title="删除任务"
                                  >
                                    <Trash2 size={18} />
                                  </button>
                                </>
                              )}
                            </div>
                          </td>
                        </motion.tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* 分页导航 */}
              {/* 翻页控件 */}
              <div className={styles.pagination}>
                <div className={styles.paginationInfo}>
                  共 {filteredTasks.length} 条记录，第 {currentPage} / {totalPages} 页
                </div>
                <div className={styles.paginationControls}>
                  <button
                    className={`${styles.paginationBtn} ${currentPage === 1 ? styles.disabled : ''}`}
                    onClick={() => setCurrentPage(1)}
                    disabled={currentPage === 1}
                  >
                    首页
                  </button>
                  <button
                    className={`${styles.paginationBtn} ${currentPage === 1 ? styles.disabled : ''}`}
                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                    disabled={currentPage === 1}
                  >
                    上一页
                  </button>
                  <div className={styles.pageNumbers}>
                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                      let pageNum;
                      if (totalPages <= 5) {
                        pageNum = i + 1;
                      } else if (currentPage <= 3) {
                        pageNum = i + 1;
                      } else if (currentPage >= totalPages - 2) {
                        pageNum = totalPages - 4 + i;
                      } else {
                        pageNum = currentPage - 2 + i;
                      }
                      return (
                        <button
                          key={pageNum}
                          className={`${styles.pageNumber} ${currentPage === pageNum ? styles.active : ''}`}
                          onClick={() => setCurrentPage(pageNum)}
                        >
                          {pageNum}
                        </button>
                      );
                    })}
                    {totalPages > 5 && currentPage < totalPages - 2 && (
                      <span className={styles.pageEllipsis}>...</span>
                    )}
                    {totalPages > 5 && currentPage >= totalPages - 2 && (
                      <button
                        className={`${styles.pageNumber} ${currentPage === totalPages ? styles.active : ''}`}
                        onClick={() => setCurrentPage(totalPages)}
                      >
                        {totalPages}
                      </button>
                    )}
                  </div>
                  <button
                    className={`${styles.paginationBtn} ${currentPage === totalPages ? styles.disabled : ''}`}
                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                    disabled={currentPage === totalPages}
                  >
                    下一页
                  </button>
                  <button
                    className={`${styles.paginationBtn} ${currentPage === totalPages ? styles.disabled : ''}`}
                    onClick={() => setCurrentPage(totalPages)}
                    disabled={currentPage === totalPages}
                  >
                    末页
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      ) : (
        /* 试卷管理 - 从任务管理界面完全复制过来的部分 */
        <div className={styles.contentSection}>
          {/* 试卷工具栏 */}
          <div className={styles.filterToolbar}>
            <div className={styles.searchInput}>
              <Search size={14} strokeWidth={1.5} />
              <input
                type="text"
                placeholder="搜索试卷..."
                value={paperSearch}
                onChange={(e) => {
                  setPaperSearch(e.target.value);
                  setCurrentPaperPage(1); // 搜索时重置到第一页
                }}
              />
            </div>
            <div className={styles.filterStats}>
              <span>共 <strong>{filteredPapers.length}</strong> 份试卷</span>
            </div>
            <div className={styles.toolbarActions}>
              {selectedPapers.length > 0 ? (
                <div className={styles.batchToolbar}>
                  <div className={styles.batchInfo}>
                    <Check size={18} />
                    已选择 <span className={styles.batchCount}>{selectedPapers.length}</span> / {filteredPapers.length} 项
                  </div>
                  <div className={styles.batchActions}>
                    <button
                      type="button"
                      className={styles.selectAllBtn}
                      onClick={selectAllPapers}
                    >
                      {selectedPapers.length === filteredPapers.length ? '取消全选' : '全选'}
                    </button>
                    {canManage && (
                      <>
                        <button
                          type="button"
                          className={styles.batchActionBtn}
                          onClick={openBatchDeleteModal}
                        >
                          <Trash2 size={16} />
                          批量删除
                        </button>
                        <button
                          type="button"
                          className={styles.cancelBatchBtn}
                          onClick={() => setSelectedPapers([])}
                        >
                          取消选择
                        </button>
                      </>
                    )}
                  </div>
                </div>
              ) : (
                <>
                  {canManage && (
                    <>
                      <button
                        type="button"
                        className={styles.createBtnSecondary}
                        onClick={openPaperModal}
                      >
                        <Plus size={16} />
                        手动创建
                      </button>
                      <button
                        type="button"
                        className={styles.generateBtn}
                        onClick={() => {
                          setGenerateMode('ai');
                          setAiGenerateMode('generate');
                          setAiConfig({
                            archive_id: 0,
                            paper_name: '',
                            question_count: 10,
                            question_types: ['single', 'multiple', 'judgment', 'essay'],
                            difficulty: 'medium',
                            total_score: 100,
                            pass_score: 60,
                            single_choice_config: { count: 0, easy: 0, medium: 0, hard: 0 },
                            multiple_choice_config: { count: 0, easy: 0, medium: 0, hard: 0 },
                            judgment_config: { count: 0, easy: 0, medium: 0, hard: 0 },
                            essay_config: { count: 0, easy: 0, medium: 0, hard: 0 },
                          });
                          setAiSelectedFileIds([]);
                          setAiExpandedArchives([]);
                          setAiArchiveSearch('');
                          setShowGeneratePaperModal(true);
                        }}
                      >
                        <Sparkles size={16} />
                        AI 生成试题
                      </button>
                      <button
                        type="button"
                        className={styles.generateBtn}
                        style={{ background: 'linear-gradient(135deg, #10b981, #059669)' }}
                        onClick={() => {
                          setGenerateMode('ai');
                          setAiGenerateMode('extract');
                          setAiConfig({
                            archive_id: 0,
                            paper_name: '',
                            question_count: 10,
                            question_types: ['single', 'multiple', 'judgment', 'essay'],
                            difficulty: 'medium',
                            total_score: 100,
                            pass_score: 60,
                            single_choice_config: { count: 0, easy: 0, medium: 0, hard: 0 },
                            multiple_choice_config: { count: 0, easy: 0, medium: 0, hard: 0 },
                            judgment_config: { count: 0, easy: 0, medium: 0, hard: 0 },
                            essay_config: { count: 0, easy: 0, medium: 0, hard: 0 },
                          });
                          setAiSelectedFileIds([]);
                          setAiExpandedArchives([]);
                          setAiArchiveSearch('');
                          setShowGeneratePaperModal(true);
                        }}
                      >
                        <FileSearch size={16} />
                        AI 识别题目
                      </button>
                    </>
                  )}
                </>
              )}
            </div>
          </div>

          {/* 试卷表格 */}
          {papersLoading ? (
            <div className={styles.loading}>
              <Loader2 className={styles.spinner} size={32} />
              <span>加载中...</span>
            </div>
          ) : filteredPapers.length === 0 ? (
            <div className={styles.empty}>
              <FileText size={48} strokeWidth={1} />
              <p>暂无试卷数据</p>
            </div>
          ) : (
            <div className={styles.tableContainer}>
              <div className={styles.tableWrapper}>
                <table className={styles.table}>
                  <thead>
                    <tr>
                      {canManage && (
                        <th className={styles.centerCell}>
                          <label className={styles.checkboxWrapper}>
                            <input
                              type="checkbox"
                              checked={selectedPapers.length === filteredPapers.length && filteredPapers.length > 0}
                              onChange={selectAllPapers}
                            />
                            <span className={styles.checkboxCustom}></span>
                          </label>
                        </th>
                      )}
                      <th>试卷名称</th>
                      <th className={styles.centerCell}>总分</th>
                      <th className={styles.centerCell}>及格分</th>
                      <th className={styles.centerCell}>时长</th>
                      <th className={styles.centerCell}>创建时间</th>
                      <th className={styles.centerCell}>操作</th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentPapers.map((paper) => (
                    <motion.tr
                      key={paper.paper_id}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      className={selectedPapers.includes(paper.paper_id) ? styles.selected : ''}
                    >
                      {canManage && (
                        <td className={styles.centerCell} data-label="选择">
                          <label className={styles.checkboxWrapper}>
                            <input
                              type="checkbox"
                              checked={selectedPapers.includes(paper.paper_id)}
                              onChange={() => togglePaperSelection(paper.paper_id)}
                            />
                            <span className={styles.checkboxCustom}>
                              <Check size={14} strokeWidth={3} />
                            </span>
                          </label>
                        </td>
                      )}
                      <td data-label="试卷名称">
                        <div className={styles.paperInfo}>
                          <div>
                            <span className={styles.paperName}>{paper.paper_name}</span>
                            {paper.paper_desc && (
                              <span className={styles.paperDesc}>{paper.paper_desc}</span>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className={styles.centerCell} data-label="总分"><span className={styles.score}>{paper.total_score} 分</span></td>
                      <td className={styles.centerCell} data-label="及格分"><span className={styles.score}>{paper.pass_score} 分</span></td>
                      <td className={styles.centerCell} data-label="时长">
                        <span className={styles.duration}>
                          {paper.exam_duration} 分钟
                        </span>
                      </td>
                      <td className={styles.centerCell} data-label="创建时间">{formatDateTime(paper.create_time)}</td>
                      <td className={styles.centerCell} data-label="操作">
                        <div className={styles.tableActions}>
                          <button
                            className={styles.tableActionBtn}
                            title="查看"
                            type="button"
                            onClick={() => openPaperViewer(paper.paper_id)}
                          >
                            <Eye size={18} />
                          </button>
                          {canManage && (
                            <>
                              <button
                                className={styles.tableActionBtn}
                                title="编辑"
                                type="button"
                                onClick={() => openPaperEditor(paper.paper_id)}
                              >
                                <Edit size={18} />
                              </button>
                              <button
                                className={`${styles.tableActionBtn} ${styles.danger}`}
                                onClick={() => handleDeletePaper(paper)}
                                title="删除"
                                type="button"
                              >
                                <Trash2 size={18} />
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </motion.tr>
                  ))}
                </tbody>
              </table>
              </div>

              {/* 分页导航 */}
              <div className={styles.pagination}>
                <div className={styles.paginationInfo}>
                  共 {filteredPapers.length} 条记录
                </div>
                <div className={styles.paginationControls}>
                  <button
                    className={`${styles.paginationBtn} ${currentPaperPage === 1 ? styles.disabled : ''}`}
                    onClick={() => setCurrentPaperPage(1)}
                    disabled={currentPaperPage === 1}
                  >
                    首页
                  </button>
                  <button
                    className={`${styles.paginationBtn} ${currentPaperPage === 1 ? styles.disabled : ''}`}
                    onClick={() => setCurrentPaperPage(prev => Math.max(prev - 1, 1))}
                    disabled={currentPaperPage === 1}
                  >
                    上一页
                  </button>
                  <div className={styles.pageNumbers}>
                    {Array.from({ length: Math.min(5, totalPaperPages) }, (_, i) => {
                      let pageNum;
                      if (totalPaperPages <= 5) {
                        pageNum = i + 1;
                      } else if (currentPaperPage <= 3) {
                        pageNum = i + 1;
                      } else if (currentPaperPage >= totalPaperPages - 2) {
                        pageNum = totalPaperPages - 4 + i;
                      } else {
                        pageNum = currentPaperPage - 2 + i;
                      }
                      return (
                        <button
                          key={pageNum}
                          className={`${styles.pageNumber} ${currentPaperPage === pageNum ? styles.active : ''}`}
                          onClick={() => setCurrentPaperPage(pageNum)}
                        >
                          {pageNum}
                        </button>
                      );
                    })}
                    {totalPaperPages > 5 && currentPaperPage < totalPaperPages - 2 && (
                      <span className={styles.pageEllipsis}>...</span>
                    )}
                    {totalPaperPages > 5 && currentPaperPage >= totalPaperPages - 2 && (
                      <button
                        className={`${styles.pageNumber} ${currentPaperPage === totalPaperPages ? styles.active : ''}`}
                        onClick={() => setCurrentPaperPage(totalPaperPages)}
                      >
                        {totalPaperPages}
                      </button>
                    )}
                  </div>
                  <button
                    className={`${styles.paginationBtn} ${currentPaperPage === totalPaperPages ? styles.disabled : ''}`}
                    onClick={() => setCurrentPaperPage(prev => Math.min(prev + 1, totalPaperPages))}
                    disabled={currentPaperPage === totalPaperPages}
                  >
                    下一页
                  </button>
                  <button
                    className={`${styles.paginationBtn} ${currentPaperPage === totalPaperPages ? styles.disabled : ''}`}
                    onClick={() => setCurrentPaperPage(totalPaperPages)}
                    disabled={currentPaperPage === totalPaperPages}
                  >
                    末页
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* 生成试卷弹窗 */}
      {showGeneratePaperModal && generateMode === 'ai' && (
        <motion.div
          className={styles.modalOverlay}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          onClick={() => setShowGeneratePaperModal(false)}
        >
          <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <div className={styles.modalTitleGroup}>
                {aiGenerateMode === 'generate' ? (
                  <Sparkles size={20} className={styles.sparkleIcon} />
                ) : (
                  <FileSearch size={20} className={styles.sparkleIcon} />
                )}
                <h2>{aiGenerateMode === 'generate' ? 'AI 生成试题' : 'AI 识别题目'}</h2>
              </div>
              <button className={styles.modalClose} onClick={() => setShowGeneratePaperModal(false)} type="button">
                <X size={20} />
              </button>
            </div>

            <div className={styles.modalBody}>
              {/* 模式提示 */}
              <div className={styles.formGroup}>
                <div className={styles.modeHint}>
                  {aiGenerateMode === 'generate' ? (
                    <>
                      <Sparkles size={16} />
                      <span>AI 生成试题模式：根据文件内容自动生成题目</span>
                    </>
                  ) : (
                    <>
                      <FileSearch size={16} />
                      <span>AI 识别题目模式：选择包含题目的文件，AI 自动识别提取，无需配置题目数量</span>
                    </>
                  )}
                </div>
              </div>

              <div className={styles.formGroup}>
                <label>试卷名称</label>
                <input
                  type="text"
                  placeholder="留空则自动生成"
                  value={aiConfig.paper_name}
                  onChange={(e) => setAiConfig({ ...aiConfig, paper_name: e.target.value })}
                />
              </div>

              {/* 本地上传区域 - 仅AI识别题目（extract）模式 */}
              {aiGenerateMode === 'extract' && (
                <div className={styles.formGroup}>
                  <label>本地上传题目文件 *</label>
                  <div className={styles.formatHint}>
                    <FileText size={12} />
                    <span>支持格式：TXT、CSV、PDF、DOCX、DOC、XLSX、XLS、PPTX、PPT、SRT。图片、视频等格式暂不支持。</span>
                  </div>
                  <input
                    type="file"
                    multiple
                    accept=".txt,.csv,.pdf,.docx,.doc,.xlsx,.xls,.pptx,.ppt,.srt"
                    onChange={handleAiFileSelect}
                    className={styles.aiFileInput}
                  />
                  {/* 已选本地文件列表 */}
                  {aiLocalFiles.length > 0 ? (
                    <div className={styles.aiLocalFileList}>
                      {aiLocalFiles.map((file, idx) => (
                        <div key={`${file.name}-${idx}`} className={styles.aiLocalFileItem}>
                          <FileText size={12} />
                          <span className={styles.aiLocalFileName}>{file.name}</span>
                          <span className={styles.aiLocalFileSize}>{formatFileSize(file.size)}</span>
                          <button
                            type="button"
                            className={styles.aiRemoveFileBtn}
                            onClick={() => removeAiLocalFile(idx)}
                            title="移除该文件"
                          >
                            <X size={10} />
                          </button>
                        </div>
                      ))}
                      <div className={styles.aiSelectedSummary}>
                        共选择 <strong>{aiLocalFiles.length}</strong> 个文件
                      </div>
                    </div>
                  ) : (
                    <div className={styles.aiEmptySelection}>
                      <FileText size={32} strokeWidth={1} />
                      <p>尚未选择任何文件</p>
                      <p className={styles.aiEmptyHint}>请点击上方按钮从本地上传题目文件</p>
                    </div>
                  )}
                </div>
              )}

              {/* 档案选择区域 - 仅AI生成试题（generate）模式 */}
              {aiGenerateMode === 'generate' && (
                <div className={styles.formGroup}>
                  <label>选择知识管理和文件 *（支持跨档案多选文件）</label>
                  <div className={styles.formatHint}>
                    <FileText size={12} />
                    <span>支持格式：TXT、CSV、PDF、DOCX、DOC、XLSX、XLS、PPTX、PPT、SRT。图片、视频等格式暂不支持。</span>
                  </div>

                  <div className={styles.aiArchiveSelector}>
                  {/* 左侧：档案搜索和选择区域 */}
                  <div className={styles.aiArchiveLeft}>
                    <div className={styles.aiSectionTitle}>
                      <BookOpen size={14} />
                      搜索并选择档案
                    </div>

                    {/* 档案搜索 */}
                    <div className={styles.searchInput} style={{ marginBottom: '8px' }}>
                      <Search size={16} strokeWidth={1.5} />
                      <input
                        type="text"
                        placeholder="搜索档案..."
                        value={aiArchiveSearch}
                        onChange={(e) => setAiArchiveSearch(e.target.value)}
                      />
                    </div>

                    {/* 档案列表（支持展开和文件选择） */}
                    <div className={styles.aiArchiveList}>
                      {archives
                        .filter(archive => archive.archive_name.toLowerCase().includes(aiArchiveSearch.toLowerCase()))
                        .map(archive => {
                          const isExpanded = aiExpandedArchives.includes(archive.archive_id);
                          const archiveSelectedFiles = aiSelectedFileIds.filter(f => f.archive_id === archive.archive_id);
                          const selectedFileCount = archiveSelectedFiles.length;

                          return (
                            <div key={archive.archive_id} className={styles.aiArchiveItem}>
                              <div
                                className={`${styles.aiArchiveHeader} ${selectedFileCount > 0 ? styles.selected : ''}`}
                                onClick={() => {
                                  // 点击档案头部，展开/收起文件列表
                                  setAiExpandedArchives(prev =>
                                    prev.includes(archive.archive_id)
                                      ? prev.filter(id => id !== archive.archive_id)
                                      : [...prev, archive.archive_id]
                                  );
                                }}
                              >
                                <div className={styles.aiArchiveInfo}>
                                  <BookOpen size={14} />
                                  <span className={styles.aiArchiveName}>{archive.archive_name}</span>
                                  <span className={styles.aiArchiveFileCount}>
                                    ({archive.files?.length || 0} 文件)
                                  </span>
                                </div>
                                <div className={styles.aiArchiveActions}>
                                  <button
                                    type="button"
                                    className={styles.aiExpandBtn}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setAiExpandedArchives(prev =>
                                        prev.includes(archive.archive_id)
                                          ? prev.filter(id => id !== archive.archive_id)
                                          : [...prev, archive.archive_id]
                                      );
                                    }}
                                  >
                                    {isExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                                  </button>
                                </div>
                              </div>

                              {/* 文件列表 */}
                              {isExpanded && archive.files && archive.files.length > 0 && (
                                <div className={styles.aiFileList}>
                                  {archive.files.map(file => {
                                    const isSelected = aiSelectedFileIds.some(f => f.file_id === file.file_id);
                                    const fileExt = getFileExtension(file.file_name);
                                    const isSupported = !fileExt || SUPPORTED_AI_EXTENSIONS.includes(fileExt);
                                    return (
                                      <div
                                        key={file.file_id}
                                        className={`${styles.aiFileItem} ${isSelected ? styles.selected : ''} ${!isSupported ? styles.unsupported : ''}`}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          if (!isSupported) return;
                                          if (isSelected) {
                                            // 取消选中
                                            setAiSelectedFileIds(prev => prev.filter(f => f.file_id !== file.file_id));
                                          } else {
                                            // 选中
                                            setAiSelectedFileIds(prev => [...prev, { file_id: file.file_id, archive_id: archive.archive_id }]);
                                          }
                                        }}
                                      >
                                        <div className={`${styles.aiFileCheck} ${!isSupported ? styles.disabled : ''}`}>
                                          {isSelected && <Check size={12} />}
                                        </div>
                                        <FileText size={12} />
                                        <span className={styles.aiFileName}>{file.file_name}</span>
                                        {!isSupported && <span className={styles.unsupportedTag}>不支持</span>}
                                      </div>
                                    );
                                  })}
                                </div>
                              )}
                            </div>
                          );
                        })}
                    </div>
                  </div>

                  {/* 右侧：已选择的档案和文件区域 */}
                  <div className={styles.aiArchiveRight}>
                    <div className={styles.aiSectionTitle}>
                      <Check size={14} />
                      已选择的档案和文件
                      {aiSelectedFileIds.length > 0 && (
                        <span className={styles.aiSelectedCount}>
                          ({aiSelectedFileIds.length} 个文件)
                        </span>
                      )}
                    </div>

                    {aiSelectedFileIds.length === 0 ? (
                      <div className={styles.aiEmptySelection}>
                        <FileText size={32} strokeWidth={1} />
                        <p>尚未选择任何文件</p>
                        <p className={styles.aiEmptyHint}>请在左侧展开档案并选择文件</p>
                      </div>
                    ) : (
                      <div className={styles.aiSelectedList}>
                        {/* 按档案分组显示已选择的文件 */}
                        {Array.from(new Set(aiSelectedFileIds.map(f => f.archive_id))).map(archiveId => {
                          const archive = archives.find(a => a.archive_id === archiveId);
                          const selectedFiles = aiSelectedFileIds.filter(f => f.archive_id === archiveId);

                          return (
                            <div key={archiveId} className={styles.aiSelectedArchive}>
                              <div className={styles.aiSelectedArchiveHeader}>
                                <BookOpen size={12} />
                                <span>{archive?.archive_name || '未知档案'}</span>
                                <span className={styles.aiSelectedArchiveCount}>
                                  ({selectedFiles.length} 个文件)
                                </span>
                                <button
                                  type="button"
                                  className={styles.aiRemoveArchiveBtn}
                                  onClick={() => {
                                    // 移除该档案下所有选中的文件
                                    setAiSelectedFileIds(prev => prev.filter(f => f.archive_id !== archiveId));
                                  }}
                                  title="移除该档案的所有文件"
                                >
                                  <X size={12} />
                                </button>
                              </div>
                              <div className={styles.aiSelectedFiles}>
                                {selectedFiles.map(fileInfo => {
                                  const file = archive?.files?.find(f => f.file_id === fileInfo.file_id);
                                  return (
                                    <div key={fileInfo.file_id} className={styles.aiSelectedFileItem}>
                                      <FileText size={10} />
                                      <span className={styles.aiSelectedFileName}>
                                        {file?.file_name || '未知文件'}
                                      </span>
                                      <button
                                        type="button"
                                        className={styles.aiRemoveFileBtn}
                                        onClick={() => {
                                          setAiSelectedFileIds(prev => prev.filter(f => f.file_id !== fileInfo.file_id));
                                        }}
                                        title="移除该文件"
                                      >
                                        <X size={10} />
                                      </button>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          );
                        })}

                        {/* 底部统计 */}
                        <div className={styles.aiSelectedSummary}>
                          共选择 <strong>{aiSelectedFileIds.length}</strong> 个文件，来自 <strong>{new Set(aiSelectedFileIds.map(f => f.archive_id)).size}</strong> 个档案
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              )}

              {/* 题型数量矩阵表格（3行×4列）- 仅在"AI生成试题"模式下显示 */}
              {aiGenerateMode === 'generate' && (
              <div className={styles.formGroup}>
                <p className={styles.matrixCaption}>每种题型请配置各难度题目数量，系统自动汇总。最多支持 50 道题。</p>
                <table className={styles.matrixGrid}>
                  <thead>
                    <tr>
                      <th></th>
                      <th>单选题</th>
                      <th>多选题</th>
                      <th>判断题</th>
                      <th>简答题</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(['easy', 'medium', 'hard'] as const).map((diff, di) => {
                      const diffLabel = ['简单', '中等', '困难'][di];
                      return (
                        <tr key={diff}>
                          <td className={styles.rowLabel}>{diffLabel}</td>
                          {(['single', 'multiple', 'judgment', 'essay'] as const).map(type => {
                            const configKey = type === 'single' ? 'single_choice_config'
                              : type === 'multiple' ? 'multiple_choice_config'
                              : type === 'judgment' ? 'judgment_config'
                              : 'essay_config';
                            const config = (aiConfig as any)[configKey] || {};
                            const val: number = config[diff] || 0;
                            return (
                              <td key={type}>
                                <input
                                  type="text"
                                  inputMode="numeric"
                                  pattern="[0-9]*"
                                  className={styles.matrixInput}
                                  value={val || ''}
                                  onChange={(e) => {
                                    const raw = e.target.value.replace(/\D/g, '');
                                    const num = parseInt(raw) || 0;
                                    const newConfig = { ...config, [diff]: num };
                                    const easy = (diff === 'easy' ? num : (config.easy || 0));
                                    const medium = (diff === 'medium' ? num : (config.medium || 0));
                                    const hard = (diff === 'hard' ? num : (config.hard || 0));
                                    newConfig.count = easy + medium + hard;
                                    setAiConfig({ ...aiConfig, [configKey]: newConfig });
                                  }}
                                />
                              </td>
                            );
                          })}
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr className={styles.subtotalRow}>
                      <td>小计</td>
                      {(['single', 'multiple', 'judgment', 'essay'] as const).map(type => {
                        const configKey = type === 'single' ? 'single_choice_config'
                          : type === 'multiple' ? 'multiple_choice_config'
                          : type === 'judgment' ? 'judgment_config'
                          : 'essay_config';
                        const count = ((aiConfig as any)[configKey]?.count) || 0;
                        return <td key={type}><strong>{count}</strong></td>;
                      })}
                    </tr>
                  </tfoot>
                </table>

                {/* 总计行 */}
                {(() => {
                  const grandTotal =
                    (aiConfig.single_choice_config?.count || 0) +
                    (aiConfig.multiple_choice_config?.count || 0) +
                    (aiConfig.judgment_config?.count || 0) +
                    (aiConfig.essay_config?.count || 0);
                  const overLimit = grandTotal > 50;
                  return (
                    <div className={styles.totalRow} style={{ color: overLimit ? '#e53e3e' : '#374151' }}>
                      <span>题目总计: <strong style={{color: overLimit ? '#e53e3e' : 'var(--cinnabar-500)'}}>{grandTotal}</strong> / 50</span>
                      {overLimit && <span style={{color: '#e53e3e', fontWeight: 700}}>⚠ 超出限制，请减少题目</span>}
                    </div>
                  );
                })()}

                {/* 满分 & 及格分配置 */}
                <div className={styles.scoreRow}>
                  <div className={styles.scoreGroup}>
                    <label>试卷满分</label>
                    <input
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      className={styles.scoreInput}
                      value={aiConfig.total_score || ''}
                      placeholder="100"
                      onChange={(e) => {
                        const raw = e.target.value.replace(/\D/g, '');
                        setAiConfig({ ...aiConfig, total_score: parseInt(raw) || 0 });
                      }}
                    />
                  </div>
                  <span className={styles.scoreDivider}>/</span>
                  <div className={styles.scoreGroup}>
                    <label>及格分</label>
                    <input
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      className={styles.scoreInput}
                      value={aiConfig.pass_score || ''}
                      placeholder="60"
                      onChange={(e) => {
                        const raw = e.target.value.replace(/\D/g, '');
                        setAiConfig({ ...aiConfig, pass_score: parseInt(raw) || 0 });
                      }}
                    />
                  </div>
                </div>
              </div>
              )}
            </div>

            <div className={styles.modalFooter}>
              {isGenerating && (
                <div className={styles.generateProgress}>
                  <div className={styles.progressBar}>
                    <div
                      className={styles.progressFill}
                      style={{ width: `${generateProgress}%` }}
                    />
                  </div>
                  <div className={styles.progressInfo}>
                    <span className={styles.progressPercent}>{generateProgress}%</span>
                    <span className={styles.progressStatus}>{generateStatus}</span>
                  </div>
                </div>
              )}
              <button
                className={styles.cancelBtn}
                onClick={(e) => {
                  e.stopPropagation();
                  handleCancelGenerate();
                }}
                type="button"
              >
                {isGenerating ? (aiGenerateMode === 'extract' ? '取消识别' : '取消生成') : '取消'}
              </button>
              <button
                className={styles.confirmBtn}
                onClick={async () => {
                  try {
                    if (aiGenerateMode === 'extract') {
                      // AI识别题目：校验本地上传文件
                      if (aiLocalFiles.length === 0) {
                        showToast('warning', '请选择至少一个本地题目文件');
                        return;
                      }
                      const unsupportedLocal = aiLocalFiles
                        .map(f => f.name)
                        .filter(name => {
                          const ext = getFileExtension(name);
                          return ext && !SUPPORTED_AI_EXTENSIONS.includes(ext);
                        });
                      if (unsupportedLocal.length > 0) {
                        showToast('error', `以下文件格式不支持 AI 识别：${unsupportedLocal.join('、')}`);
                        return;
                      }
                    } else {
                      // AI生成试题：校验档案文件
                      if (aiSelectedFileIds.length === 0) {
                        showToast('warning', '请选择至少一个文件');
                        return;
                      }
                      const unsupported = getUnsupportedFiles(aiSelectedFileIds);
                      if (unsupported.length > 0) {
                        showToast('error', `以下文件格式不支持 AI 生成：${unsupported.join('、')}`);
                        return;
                      }
                    }
                    await handleGeneratePaper();
                  } catch (error) {
                    console.error('生成试卷按钮点击错误:', error);
                    showToast('error', '生成试卷时发生错误，请稍后重试');
                  }
                }}
                disabled={isGenerating}
                type="button"
              >
                {isGenerating ? (
                  <>
                    <Loader2 size={16} className={styles.spinner} />
                    {aiGenerateMode === 'extract' ? '识别中...' : '生成中...'}
                  </>
                ) : (
                  <>
                    {aiGenerateMode === 'extract' ? <FileSearch size={16} /> : <Sparkles size={16} />}
                    {aiGenerateMode === 'extract' ? '开始识别' : '开始生成'}
                  </>
                )}
              </button>
            </div>
          </div>
        </motion.div>
      )}

      {/* 手动创建试卷弹窗 */}
      {isPaperModalOpen && (
        <motion.div
          className={styles.modalOverlay}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          onClick={closePaperModal}
        >
          <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h2>手动创建试卷</h2>
              <button className={styles.modalClose} onClick={closePaperModal} type="button">
                <X size={20} />
              </button>
            </div>

            <div className={styles.modalBody}>
              <div className={styles.formGroup}>
                <label>试卷名称 *</label>
                <input
                  type="text"
                  placeholder="请输入试卷名称"
                  value={paperForm.paper_name}
                  onChange={(e) => setPaperForm({ ...paperForm, paper_name: e.target.value })}
                />
              </div>

              <div className={styles.formGroup}>
                <label>试卷描述</label>
                <textarea
                  placeholder="请输入试卷描述（可选）"
                  value={paperForm.paper_desc}
                  onChange={(e) => setPaperForm({ ...paperForm, paper_desc: e.target.value })}
                  rows={3}
                />
              </div>

              <div className={styles.formRow}>
                <div className={styles.formGroup}>
                  <label>总分</label>
                  <input
                    type="number"
                    min="1"
                    max="1000"
                    value={paperForm.total_score}
                    onChange={(e) => setPaperForm({ ...paperForm, total_score: parseInt(e.target.value) || 100 })}
                  />
                </div>
                <div className={styles.formGroup}>
                  <label>及格分</label>
                  <input
                    type="number"
                    min="1"
                    max={paperForm.total_score}
                    value={paperForm.pass_score}
                    onChange={(e) => setPaperForm({ ...paperForm, pass_score: parseInt(e.target.value) || 60 })}
                  />
                </div>
                <div className={styles.formGroup}>
                  <label>考试时长 (分钟)</label>
                  <input
                    type="number"
                    min="1"
                    max="300"
                    value={paperForm.exam_duration}
                    onChange={(e) => setPaperForm({ ...paperForm, exam_duration: parseInt(e.target.value) || 60 })}
                  />
                </div>
              </div>
            </div>

            <div className={styles.modalFooter}>
              <button className={styles.cancelBtn} onClick={closePaperModal} type="button">
                取消
              </button>
              <button
                className={styles.confirmBtn}
                onClick={handleCreatePaper}
                disabled={savingPaper}
                type="button"
              >
                {savingPaper ? (
                  <>
                    <Loader2 size={16} className={styles.spinner} />
                    创建中...
                  </>
                ) : (
                  '创建试卷'
                )}
              </button>
            </div>
          </div>
        </motion.div>
      )}

      {/* 试卷编辑器 */}
      {isPaperEditorOpen && editingPaper && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 1000 }}>
          <PaperEditor
            paper={editingPaper}
            onSave={handleSavePaper}
            onCancel={() => {
              setIsPaperEditorOpen(false);
              setEditingPaper(null);
            }}
          />
        </div>
      )}

      {/* 试卷查看弹窗 */}
      {isPaperViewerOpen && viewingPaper && (
        <div className={styles.modalOverlay} onClick={() => setIsPaperViewerOpen(false)}>
          <div className={styles.viewerModal} onClick={e => e.stopPropagation()}>
            <div className={styles.viewerHeader}>
              <div>
                <h2>{viewingPaper.paper_name}</h2>
                <div className={styles.viewerMeta}>
                  <span><BookOpen size={14} /> {viewingPaper.questions?.length || 0} 题</span>
                  <span>总分: {viewingPaper.total_score} 分</span>
                  <span>时长: {viewingPaper.exam_duration} 分钟</span>
                  {viewingPaper.paper_desc && (
                    <span className={styles.viewerDesc}>{viewingPaper.paper_desc}</span>
                  )}
                </div>
              </div>
              <button
                className={styles.modalClose}
                onClick={() => setIsPaperViewerOpen(false)}
                type="button"
              >
                <X size={20} />
              </button>
            </div>
            <div className={styles.viewerContent}>
              {viewingPaper.questions && viewingPaper.questions.length > 0 ? (
                viewingPaper.questions.map((q, index) => (
                  <div key={q.question_id || index} className={styles.questionCard}>
                    <div className={styles.questionHeader}>
                      <span className={styles.questionNumber}>第 {index + 1} 题</span>
                      <span className={styles.questionType} data-type={q.question_type}>
                        {['judgment', 'judge', 'boolean'].includes(q.question_type) ? '判断题' :
                         ['multiple', 'multi'].includes(q.question_type) ? '多选题' :
                         q.question_type === 'single' ? '单选题' : '简答题'}
                      </span>
                      {q.difficulty && DIFFICULTY_CONFIG[q.difficulty] && (
                        <span
                          className={styles.questionDifficulty}
                          style={{
                            color: DIFFICULTY_CONFIG[q.difficulty].color,
                            backgroundColor: DIFFICULTY_CONFIG[q.difficulty].bgColor,
                          }}
                        >
                          {DIFFICULTY_CONFIG[q.difficulty].label}
                        </span>
                      )}
                      <span className={styles.questionScore}>{q.score} 分</span>
                    </div>
                    <div className={styles.questionContent}>{q.question_content}</div>
                    {q.options && Object.keys(q.options).length > 0 && !['essay', 'short'].includes(q.question_type) && (
                      <div className={styles.questionOptions}>
                        {Object.entries(q.options as Record<string, string>)
                          .filter(([, v]) => v?.trim())
                          .map(([key, value]) => (
                            <div
                              key={key}
                              className={`${styles.optionItem} ${(q.correct_answer.includes(key) || q.correct_answer === value) ? styles.correct : ''}`}
                            >
                              <span className={styles.optionKey}>{key}.</span>
                              <span>{value}</span>
                              {(q.correct_answer.includes(key) || q.correct_answer === value) && (
                                <span className={styles.correctBadge}>✓</span>
                              )}
                            </div>
                          ))}
                      </div>
                    )}
                    {/* 判断题无选项时自动生成正确/错误选项 */}
                    {['judgment', 'judge', 'boolean'].includes(q.question_type) && (!q.options || Object.keys(q.options).length === 0) && (
                      <div className={styles.questionOptions}>
                        {(['A', 'B'] as const).map((key) => {
                          const label = key === 'A' ? '正确' : '错误';
                          const isCorrect = q.correct_answer === label || q.correct_answer === key;
                          return (
                            <div
                              key={key}
                              className={`${styles.optionItem} ${isCorrect ? styles.correct : ''}`}
                            >
                              <span className={styles.optionKey}>{key}.</span>
                              <span>{label}</span>
                              {isCorrect && (
                                <span className={styles.correctBadge}>✓</span>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                    {['essay', 'short'].includes(q.question_type) && (
                      <div className={styles.essayAnswer}>
                        <span className={styles.answerLabel}>参考答案：</span>
                        <p>{q.correct_answer}</p>
                      </div>
                    )}
                  </div>
                ))
              ) : (
                <div className={styles.emptyState}>
                  <FileText size={48} />
                  <p>暂无题目</p>
                </div>
              )}
            </div>
            <div className={styles.viewerFooter}>
              <button
                type="button"
                className={styles.primaryBtn}
                onClick={() => {
                  setIsPaperViewerOpen(false);
                  openPaperEditor(viewingPaper.paper_id);
                }}
              >
                <Edit size={16} />
                编辑试卷
              </button>
              <button
                type="button"
                className={styles.secondaryBtn}
                onClick={() => setIsPaperViewerOpen(false)}
              >
                关闭
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 试卷信息查看弹窗（只显示基本信息，不显示题目） */}
      {isPaperInfoOpen && viewingPaperInfo && (
        <div className={styles.modalOverlay} onClick={() => setIsPaperInfoOpen(false)}>
          <div className={styles.modal} onClick={e => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h2>试卷信息</h2>
              <button
                className={styles.modalClose}
                onClick={() => setIsPaperInfoOpen(false)}
                type="button"
              >
                <X size={20} />
              </button>
            </div>
            <div className={styles.modalBody}>
              <div className={styles.archiveInfoGrid}>
                <div className={styles.infoItem}>
                  <span className={styles.infoLabel}>试卷名称</span>
                  <span className={styles.infoValue}>{viewingPaperInfo.paper_name}</span>
                </div>
                <div className={styles.infoItem}>
                  <span className={styles.infoLabel}>总分</span>
                  <span className={styles.infoValue}>{viewingPaperInfo.total_score} 分</span>
                </div>
                <div className={styles.infoItem}>
                  <span className={styles.infoLabel}>及格分数</span>
                  <span className={styles.infoValue}>{viewingPaperInfo.pass_score} 分</span>
                </div>
                <div className={styles.infoItem}>
                  <span className={styles.infoLabel}>考试时长</span>
                  <span className={styles.infoValue}>{viewingPaperInfo.exam_duration} 分钟</span>
                </div>
                <div className={styles.infoItem}>
                  <span className={styles.infoLabel}>创建时间</span>
                  <span className={styles.infoValue}>{formatDateTime(viewingPaperInfo.create_time)}</span>
                </div>
                <div className={styles.infoItem}>
                  <span className={styles.infoLabel}>更新时间</span>
                  <span className={styles.infoValue}>{formatDateTime(viewingPaperInfo.update_time)}</span>
                </div>
              </div>
              {viewingPaperInfo.paper_desc && (
                <div className={styles.formGroup} style={{ marginTop: 'var(--space-4)' }}>
                  <label className={styles.formLabel}>试卷描述</label>
                  <div className={styles.infoValue} style={{
                    padding: 'var(--space-3)',
                    background: 'var(--paper-100)',
                    borderRadius: 'var(--radius-md)',
                    fontSize: 'var(--text-sm)'
                  }}>
                    {viewingPaperInfo.paper_desc}
                  </div>
                </div>
              )}
            </div>
            <div className={styles.modalFooter}>
              <button
                type="button"
                className={styles.cancelBtn}
                onClick={() => setIsPaperInfoOpen(false)}
              >
                关闭
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 批量删除确认弹窗 */}
      {isBatchDeleteModalOpen && (
        <div className={styles.modalOverlay} onClick={closeBatchDeleteModal}>
          <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h2>确认批量删除</h2>
              <button className={styles.modalClose} onClick={closeBatchDeleteModal} type="button">
                <X size={20} />
              </button>
            </div>
            <div className={styles.modalBody}>
              <p>确定要删除选中的 {selectedPapers.length} 项吗？此操作不可恢复。</p>
            </div>
            <div className={styles.modalFooter}>
              <button className={styles.cancelBtn} onClick={closeBatchDeleteModal} type="button">
                取消
              </button>
              <button
                className={`${styles.confirmBtn} ${styles.danger}`}
                onClick={handleBatchDelete}
                type="button"
              >
                确认删除
              </button>
            </div>
          </div>
        </div>
      )}
      {/* 任务详情弹窗 */}
      {isTaskDetailOpen && selectedTaskDetail && (
        <motion.div
          className={styles.modalOverlay}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          onClick={() => setIsTaskDetailOpen(false)}
        >
          <motion.div
            className={styles.taskDetailModal}
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className={styles.modalHeader}>
              <div>
                <h2>{selectedTaskDetail.task_name}</h2>
                <p className={styles.modalSubtitle}>
                  {selectedTaskDetail.task_type === 'learning' ? '学习任务' : '考试任务'} ·
                  共 {selectedTaskDetail.total_students} 人 ·
                  已完成 {selectedTaskDetail.completed_count} 人
                </p>
              </div>
              <button className={styles.modalClose} onClick={() => setIsTaskDetailOpen(false)}>
                <X size={20} />
              </button>
            </div>
            <div className={styles.taskDetailBody}>
              {/* 任务基本信息 */}
              <div className={styles.taskInfoSection}>
                <h3>任务信息</h3>
                <div className={styles.taskInfoGrid}>
                  {selectedTaskDetail.task_type === 'learning' ? (
                    <>
                      <div className={styles.infoItem}>
                        <span className={styles.infoLabel}>截止时间</span>
                        <span className={styles.infoValue}>
                          {formatShortDateTime((selectedTaskDetail.task_info as LearningTask).deadline)}
                        </span>
                      </div>
                      <div className={styles.infoItem}>
                        <span className={styles.infoLabel}>任务描述</span>
                        <span className={styles.infoValue}>
                          {(selectedTaskDetail.task_info as LearningTask).task_desc || '无'}
                        </span>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className={styles.infoItem}>
                        <span className={styles.infoLabel}>考试开始</span>
                        <span className={styles.infoValue}>
                          {formatShortDateTime((selectedTaskDetail.task_info as ExamTask).exam_start_time)}
                        </span>
                      </div>
                      <div className={styles.infoItem}>
                        <span className={styles.infoLabel}>考试结束</span>
                        <span className={styles.infoValue}>
                          {formatShortDateTime((selectedTaskDetail.task_info as ExamTask).exam_end_time)}
                        </span>
                      </div>
                      <div className={styles.infoItem}>
                        <span className={styles.infoLabel}>试卷</span>
                        <span className={styles.infoValue}>
                          {(selectedTaskDetail.task_info as ExamTask).paper?.paper_name || '-'}
                        </span>
                      </div>
                    </>
                  )}
                </div>
              </div>

              {/* 关联文件信息 - 仅学习任务显示 */}
              {selectedTaskDetail.task_type === 'learning' && (
                <div className={styles.taskFilesSection}>
                  <h3>关联文件</h3>
                  {viewDetailFilesLoading ? (
                    <div className={styles.filesLoading}>加载中...</div>
                  ) : viewDetailFiles.length > 0 ? (
                    <div className={styles.fileListInDetail}>
                      {viewDetailFiles.map((file) => (
                        <div key={file.file_id} className={styles.fileItemInDetail}>
                          <div className={styles.fileHeaderInDetail}>
                            <FileText size={16} className={styles.fileIconInline} />
                            <span className={styles.fileNameInDetail}>{file.file_name}</span>
                            <span className={styles.fileTypeInDetail}>
                              {file.file_type?.toUpperCase()}
                            </span>
                          </div>
                          <div className={styles.fileDetailsInDetail}>
                            <span className={styles.archiveSourceInDetail}>
                              来源：{file.archive_name || '未知档案'}
                            </span>
                            <span className={styles.durationInDetail}>
                              需学习时长：{Math.floor((file.required_duration || 60) / 60)} 分钟
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className={styles.emptyFiles}>暂无关联文件</div>
                  )}
                </div>
              )}

              {/* 学生完成情况 */}
              <div className={styles.studentsSection}>
                <h3>学生完成情况</h3>
                <div className={styles.studentsTableContainer}>
                  <table className={styles.studentsTable}>
                    <thead>
                      <tr>
                        <th>学生姓名</th>
                        <th className={styles.centerCell}>账号</th>
                        <th className={styles.centerCell}>分配时间</th>
                        <th className={styles.centerCell}>完成状态</th>
                        {selectedTaskDetail.task_type === 'exam' ? (
                          <>
                            <th className={styles.centerCell}>得分</th>
                            <th className={styles.centerCell}>是否通过</th>
                            <th className={styles.centerCell}>提交时间</th>
                          </>
                        ) : (
                          <>
                            <th className={styles.centerCell}>学习进度</th>
                            <th className={styles.centerCell}>学习时长</th>
                          </>
                        )}
                      </tr>
                    </thead>
                    <tbody>
                      {selectedTaskDetail.students?.map((student: any) => (
                        <tr key={student.user_id}>
                          <td>{student.user_name}</td>
                          <td className={styles.centerCell}>{student.account}</td>
                          <td className={styles.centerCell}>{formatShortDateTime(student.assign_time)}</td>
                          <td className={styles.centerCell}>
                            <span className={`${styles.completionStatus} ${student.is_completed ? styles.completed : styles.incomplete}`}>
                              {student.is_completed ? '已完成' : '未完成'}
                            </span>
                          </td>
                          {selectedTaskDetail.task_type === 'exam' ? (
                            <>
                              <td className={styles.centerCell}>{student.score !== null ? `${student.score} 分` : '-'}</td>
                              <td className={styles.centerCell}>
                                {student.is_passed === null ? '-' : (
                                  <span className={`${styles.passStatus} ${student.is_passed ? styles.passed : styles.failed}`}>
                                    {student.is_passed ? '通过' : '未通过'}
                                  </span>
                                )}
                              </td>
                              <td className={styles.centerCell}>{student.submit_time ? formatShortDateTime(student.submit_time) : '-'}</td>
                            </>
                          ) : (
                            <>
                              <td className={styles.centerCell}>
                                <div className={styles.miniProgressBar}>
                                  <div
                                    className={styles.miniProgressFill}
                                    style={{ width: `${student.progress}%` }}
                                  />
                                </div>
                                <span className={styles.progressPercent}>{student.progress}%</span>
                              </td>
                              <td className={styles.centerCell}>{student.learn_duration ? `${Math.round(student.learn_duration / 60)} 分钟` : '-'}</td>
                            </>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
            <div className={styles.modalFooter}>
              <button className={styles.cancelBtn} onClick={() => setIsTaskDetailOpen(false)}>
                关闭
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}

      {/* 编辑任务弹窗 */}
      {isEditTaskOpen && editingTask && (
        <motion.div
          className={styles.modalOverlay}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          onClick={() => setIsEditTaskOpen(false)}
        >
          <motion.div
            className={styles.modal}
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className={styles.modalHeader}>
              <h2>编辑{editingTask.task_type === 'learning' ? '学习任务' : '考试任务'}</h2>
              <button className={styles.modalClose} onClick={() => setIsEditTaskOpen(false)}>
                <X size={20} />
              </button>
            </div>
            <div className={styles.modalBody}>
              {/* 任务名称 */}
              <div className={styles.formField}>
                <label className={styles.formLabel}>任务名称</label>
                <input
                  type="text"
                  className={styles.formInput}
                  value={editTaskForm.task_name}
                  onChange={(e) => setEditTaskForm({ ...editTaskForm, task_name: e.target.value })}
                  placeholder="请输入任务名称"
                />
              </div>

              {/* 学习任务：截止时间 */}
              {editingTask.task_type === 'learning' && (
                <div className={styles.formField}>
                  <label className={styles.formLabel}>截止时间</label>
                  <input
                    type="datetime-local"
                    className={styles.formInput}
                    value={editTaskForm.deadline}
                    onChange={(e) => setEditTaskForm({ ...editTaskForm, deadline: e.target.value })}
                  />
                </div>
              )}

              {/* 考试任务：考试时间 */}
              {editingTask.task_type === 'exam' && (
                <>
                  <div className={styles.formField}>
                    <label className={styles.formLabel}>考试开始时间</label>
                    <input
                      type="datetime-local"
                      className={styles.formInput}
                      value={editTaskForm.exam_start_time}
                      onChange={(e) => setEditTaskForm({ ...editTaskForm, exam_start_time: e.target.value })}
                    />
                  </div>
                  <div className={styles.formField}>
                    <label className={styles.formLabel}>考试结束时间</label>
                    <input
                      type="datetime-local"
                      className={styles.formInput}
                      value={editTaskForm.exam_end_time}
                      onChange={(e) => setEditTaskForm({ ...editTaskForm, exam_end_time: e.target.value })}
                    />
                  </div>
                </>
              )}

              {/* 任务描述 */}
              <div className={styles.formField}>
                <label className={styles.formLabel}>任务描述</label>
                <textarea
                  className={styles.formInput}
                  value={editTaskForm.task_desc}
                  onChange={(e) => setEditTaskForm({ ...editTaskForm, task_desc: e.target.value })}
                  placeholder="请输入任务描述（可选）"
                  rows={3}
                />
              </div>

              {/* 学习任务：关联文档信息 */}
              {editingTask.task_type === 'learning' && (
                <div className={styles.formField}>
                  <label className={styles.formLabel}>关联文档</label>
                  {filesLoading ? (
                    <div className={styles.loadingText}>加载中...</div>
                  ) : editTaskFiles.length > 0 ? (
                    <div className={styles.fileListInEdit}>
                      {editTaskFiles.map((file) => (
                        <div key={file.file_id} className={styles.fileItemInEdit}>
                          <div className={styles.fileHeader}>
                            <FileText size={16} className={styles.fileIconInline} />
                            <div className={styles.fileInfoInline}>
                              <span className={styles.fileNameText}>{file.file_name}</span>
                              <span className={styles.fileTypeText}>
                                {file.file_type?.toUpperCase()} · {file.file_size ? `${(file.file_size / 1024).toFixed(1)} KB` : ''}
                              </span>
                            </div>
                          </div>
                          <div className={styles.fileDetailsRow}>
                            <span className={styles.archiveSource}>
                              来源：<strong>{file.archive_name || '未知档案'}</strong>
                            </span>
                            {isDocumentFileType(file.file_type) && (
                              <div className={styles.durationInputWrapper}>
                                <span className={styles.durationLabel}>需学习时长：</span>
                                <input
                                  type="number"
                                  min="1"
                                  value={Math.floor((file.required_duration || 60) / 60)}
                                  onChange={(e) => {
                                    const minutes = Math.max(1, parseInt(e.target.value) || 1);
                                    // 更新本地状态
                                    setEditTaskFiles(prev =>
                                      prev.map(f =>
                                        f.file_id === file.file_id
                                          ? { ...f, required_duration: minutes * 60 }
                                          : f
                                      )
                                    );
                                  }}
                                  className={styles.durationInput}
                                />
                                <span className={styles.durationUnit}>分钟</span>
                              </div>
                            )}
                            {!isDocumentFileType(file.file_type) && (
                              <span className={styles.videoDurationNoteSmall}>
                                视频文件学习时长为视频本身时长
                              </span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className={styles.emptyText}>暂无关联文档</div>
                  )}
                </div>
              )}

              {/* 学生管理 */}
              <div className={styles.studentManagementSection}>
                <h3>管理分配的学生</h3>

                <div className={styles.studentManagementContainer}>
                  {/* 已分配学生列表 */}
                  <div className={styles.assignedStudentsSection}>
                    <div className={styles.sectionHeader}>
                      <div className={styles.searchAndSelectHeader}>
                        <div className={styles.searchAndActions}>
                          <h4>已分配的学生 ({assignedStudents.length})</h4>
                          <div className={styles.searchBoxSmall}>
                            <input
                              type="text"
                              className={styles.searchInputSmall}
                              placeholder="搜索姓名/账号/部门..."
                              value={assignedSearch}
                              onChange={(e) => setAssignedSearch(e.target.value)}
                            />
                          </div>
                        </div>
                        {assignedStudents.length > 0 && (
                          <div className={styles.selectButtons}>
                            <button
                              className={styles.selectAllBtn}
                              onClick={() => {
                                const filteredStudents = assignedStudents.filter(
                                  (student) =>
                                    student.user_name.toLowerCase().includes(assignedSearch.toLowerCase()) ||
                                    student.account.toLowerCase().includes(assignedSearch.toLowerCase()) ||
                                    (student.department || '').toLowerCase().includes(assignedSearch.toLowerCase())
                                );
                                setSelectedToRemove(filteredStudents.map(s => s.user_id));
                              }}
                              disabled={filteredAssignedStudents.length === 0}
                            >
                              全选
                            </button>
                            <button
                              className={styles.clearBtn}
                              onClick={() => setSelectedToRemove([])}
                              disabled={selectedToRemove.length === 0}
                            >
                              清空
                            </button>
                          </div>
                        )}
                      </div>
                      {assignedStudents.length > 0 && (
                        <button
                          className={styles.removeBtn}
                          onClick={handleRemoveStudents}
                          disabled={selectedToRemove.length === 0}
                        >
                          移除 ({selectedToRemove.length})
                        </button>
                      )}
                    </div>
                    <div className={styles.studentsList}>
                      {studentsLoading ? (
                        <div className={styles.loadingSpinner}>加载中...</div>
                      ) : assignedStudents.length === 0 ? (
                        <div className={styles.noStudents}>暂无已分配的学生</div>
                      ) : (
                        <div className={styles.studentsGrid}>
                          {filteredAssignedStudents.map((student) => (
                            <div
                              key={student.user_id}
                              className={`${styles.studentItem} ${
                                selectedToRemove.includes(student.user_id) ? styles.selected : ''
                              }`}
                              onClick={() => {
                                setSelectedToRemove(prev =>
                                  prev.includes(student.user_id)
                                    ? prev.filter(id => id !== student.user_id)
                                    : [...prev, student.user_id]
                                );
                              }}
                            >
                              <input
                                type="checkbox"
                                checked={selectedToRemove.includes(student.user_id)}
                                onChange={() => {}}
                                className={styles.checkbox}
                              />
                              <div className={styles.studentInfo}>
                                <div className={styles.studentName}>{student.user_name}</div>
                                <div className={styles.studentAccount}>{student.account}</div>
                                <div className={styles.studentMeta}>
                                  <span className={styles.studentRole}>角色: 学生</span>
                                  <span className={styles.studentDept}>部门: {student.department || '未分配'}</span>
                                </div>
                                <div className={styles.assignTime}>
                                  {student.assign_time ? `分配于: ${new Date(student.assign_time).toLocaleString('zh-CN')}` : '待分配'}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* 可添加的学生列表 */}
                  <div className={styles.availableStudentsSection}>
                    <div className={styles.sectionHeader}>
                      <div className={styles.searchAndSelectHeader}>
                        <div className={styles.searchAndActions}>
                          <h4>可添加的学生 ({availableStudents.length})</h4>
                          <div className={styles.searchBoxSmall}>
                            <input
                              type="text"
                              className={styles.searchInputSmall}
                              placeholder="搜索姓名/账号/部门..."
                              value={availableSearch}
                              onChange={(e) => setAvailableSearch(e.target.value)}
                            />
                          </div>
                        </div>
                        {availableStudents.length > 0 && (
                          <div className={styles.selectButtons}>
                            <button
                              className={styles.selectAllBtn}
                              onClick={() => {
                                const filteredStudents = availableStudents.filter(
                                  (student) =>
                                    student.user_name.toLowerCase().includes(availableSearch.toLowerCase()) ||
                                    student.account.toLowerCase().includes(availableSearch.toLowerCase()) ||
                                    (student.dept_name || '').toLowerCase().includes(availableSearch.toLowerCase())
                                );
                                setSelectedToAdd(filteredStudents.map(s => s.user_id));
                              }}
                              disabled={filteredAvailableStudents.length === 0}
                            >
                              全选
                            </button>
                            <button
                              className={styles.clearBtn}
                              onClick={() => setSelectedToAdd([])}
                              disabled={selectedToAdd.length === 0}
                            >
                              清空
                            </button>
                          </div>
                        )}
                      </div>
                      {availableStudents.length > 0 && (
                        <button
                          className={styles.addBtn}
                          onClick={handleAddStudents}
                          disabled={selectedToAdd.length === 0}
                        >
                          添加 ({selectedToAdd.length})
                        </button>
                      )}
                    </div>
                    <div className={styles.studentsList}>
                      {studentsLoading ? (
                        <div className={styles.loadingSpinner}>加载中...</div>
                      ) : availableStudents.length === 0 ? (
                        <div className={styles.noStudents}>没有可添加的学生</div>
                      ) : (
                        <div className={styles.studentsGrid}>
                          {filteredAvailableStudents.map((student) => (
                            <div
                              key={student.user_id}
                              className={`${styles.studentItem} ${
                                selectedToAdd.includes(student.user_id) ? styles.selected : ''
                              }`}
                              onClick={() => {
                                setSelectedToAdd(prev =>
                                  prev.includes(student.user_id)
                                    ? prev.filter(id => id !== student.user_id)
                                    : [...prev, student.user_id]
                                );
                              }}
                            >
                              <input
                                type="checkbox"
                                checked={selectedToAdd.includes(student.user_id)}
                                onChange={() => {}}
                                className={styles.checkbox}
                              />
                              <div className={styles.studentInfo}>
                                <div className={styles.studentName}>{student.user_name}</div>
                                <div className={styles.studentAccount}>{student.account}</div>
                                <div className={styles.studentMeta}>
                                  <span className={styles.studentRole}>角色: {student.role_name || '学生'}</span>
                                  <span className={styles.studentDept}>部门: {student.dept_name || '未分配'}</span>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className={styles.modalFooter}>
              <button className={styles.cancelBtn} onClick={() => setIsEditTaskOpen(false)}>
                取消
              </button>
              <button
                className={styles.confirmBtn}
                onClick={handleSaveTaskEdit}
                disabled={savingTask}
              >
                {savingTask ? (
                  <>
                    <Loader2 size={16} className={styles.spinner} />
                    保存中...
                  </>
                ) : (
                  <>
                    <Check size={16} />
                    保存
                  </>
                )}
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}

      {/* 查看知识管理详情弹窗 */}
      {isViewFilesModalOpen && selectedArchiveForFiles && (
        <motion.div
          className={styles.modalOverlay}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          onClick={() => setIsViewFilesModalOpen(false)}
        >
          <motion.div
            className={`${styles.modal} ${styles.archiveDetailModal}`}
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className={styles.modalHeader}>
              <div>
                <h2>{selectedArchiveForFiles.archive_name}</h2>
                <p className={styles.modalSubtitle}>
                  知识管理详情
                </p>
              </div>
              <button className={styles.modalClose} onClick={() => setIsViewFilesModalOpen(false)}>
                <X size={20} />
              </button>
            </div>
            <div className={styles.modalBody}>
              {/* 档案基本信息 */}
              <div className={styles.archiveInfoSection}>
                <h3>基本信息</h3>
                <div className={styles.archiveInfoGrid}>
                  <div className={styles.infoItem}>
                    <span className={styles.infoLabel}>档案描述</span>
                    <span className={styles.infoValue}>
                      {selectedArchiveForFiles.archive_desc || '暂无描述'}
                    </span>
                  </div>
                  <div className={styles.infoItem}>
                    <span className={styles.infoLabel}>标签</span>
                    <span className={styles.infoValue}>
                      {selectedArchiveForFiles.tags && selectedArchiveForFiles.tags.length > 0
                        ? selectedArchiveForFiles.tags.map((tag, i) => (
                            <span key={i} className={styles.tag}>{tag}</span>
                          ))
                        : '无标签'}
                    </span>
                  </div>
                  <div className={styles.infoItem}>
                    <span className={styles.infoLabel}>上传者</span>
                    <span className={styles.infoValue}>
                      {selectedArchiveForFiles.uploader_name || '-'}
                    </span>
                  </div>
                  <div className={styles.infoItem}>
                    <span className={styles.infoLabel}>上传时间</span>
                    <span className={styles.infoValue}>
                      {formatDateTime(selectedArchiveForFiles.upload_time)}
                    </span>
                  </div>
                  <div className={styles.infoItem}>
                    <span className={styles.infoLabel}>处理状态</span>
                    <span className={`${styles.infoValue} ${styles.processStatus} ${
                      selectedArchiveForFiles.process_status === 2 ? styles.completed :
                      selectedArchiveForFiles.process_status === 1 ? styles.processing :
                      selectedArchiveForFiles.process_status === 3 ? styles.failed : styles.pending
                    }`}>
                      {selectedArchiveForFiles.process_status === 2 ? '已完成' :
                       selectedArchiveForFiles.process_status === 1 ? '处理中' :
                       selectedArchiveForFiles.process_status === 3 ? '处理失败' : '待处理'}
                    </span>
                  </div>
                  <div className={styles.infoItem}>
                    <span className={styles.infoLabel}>可见性</span>
                    <span className={styles.infoValue}>
                      {selectedArchiveForFiles.visibility === 2 ? '公开' :
                       selectedArchiveForFiles.visibility === 1 ? '部门可见' : '私有'}
                    </span>
                  </div>
                  {selectedArchiveForFiles.process_error && (
                    <div className={styles.infoItem}>
                      <span className={styles.infoLabel}>错误信息</span>
                      <span className={`${styles.infoValue} ${styles.errorText}`}>
                        {selectedArchiveForFiles.process_error}
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {/* 文件列表 */}
              <div className={styles.archiveFilesSection}>
                <h3>文件列表 ({selectedArchiveForFiles.files?.length || 0} 个)</h3>
                {selectedArchiveForFiles.files && selectedArchiveForFiles.files.length > 0 ? (
                  <div className={styles.fileList}>
                    {selectedArchiveForFiles.files.map((file, index) => (
                      <div key={index} className={styles.fileItem}>
                        <div className={styles.fileInfo}>
                          <span className={styles.fileName}>{file.file_name}</span>
                          <div className={styles.fileMeta}>
                            <span className={styles.fileType}>{file.file_type?.toUpperCase()}</span>
                            <span>{file.file_size ? `${(file.file_size / 1024 / 1024).toFixed(2)} MB` : ''}</span>
                            <span>上传于 {formatDateTime(file.upload_time)}</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className={styles.emptyState}>
                    <FileText size={32} />
                    <p>该知识管理中暂无文件</p>
                  </div>
                )}
              </div>
            </div>
            <div className={styles.modalFooter}>
              <button className={styles.cancelBtn} onClick={() => setIsViewFilesModalOpen(false)}>
                关闭
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}

    </div>
  );
}